import { BM, } from './backend';
import { attachInspector, detachInspector, performanceMeasureEnd, performanceMeasureStart, } from './dev_tools';
import { Event, EventTarget, } from './event';
import { ENV, globalOptions } from './global_options';
import { MutationObserverTarget } from './mutation_observer';
import { ParsedSelector } from './selector';
import { ELEMENT_SYMBOL, isComponent, isElement, isNativeNode, isShadowRoot, isVirtualNode, } from './type_symbol';
/**
 * The "style" attribute and class list segments
 *
 * This allows different modules set the "style" attribute or the class list of an element
 * without overriding each other.
 * The final value is the concat of all segments.
 * When calling `setNodeStyle` or `setNodeClass` on an element,
 * a segment can be specified.
 */
export var StyleSegmentIndex;
(function (StyleSegmentIndex) {
    /** The main style segment, generally managed by the template engine (or manually set) */
    StyleSegmentIndex[StyleSegmentIndex["MAIN"] = 0] = "MAIN";
    /** The template style segment, preserved for template engine */
    StyleSegmentIndex[StyleSegmentIndex["TEMPLATE_EXTRA"] = 1] = "TEMPLATE_EXTRA";
    /** The animation style segment, preserved for temporary transition */
    StyleSegmentIndex[StyleSegmentIndex["ANIMATION_EXTRA"] = 2] = "ANIMATION_EXTRA";
    /** The temporary style segment, preserved for high priority styles */
    StyleSegmentIndex[StyleSegmentIndex["TEMP_EXTRA"] = 3] = "TEMP_EXTRA";
})(StyleSegmentIndex || (StyleSegmentIndex = {}));
const DESTROYED_SHADOW_BACKEND_CONTEXT = {
    mode: 1 /* BackendMode.Shadow */,
    destroyed: true,
    dropBackendAfterRelease: true,
};
const DESTROYED_COMPOSED_BACKEND_CONTEXT = {
    mode: 2 /* BackendMode.Composed */,
    destroyed: true,
    dropBackendAfterRelease: true,
};
const DESTROYED_DOMLIKE_BACKEND_CONTEXT = {
    mode: 3 /* BackendMode.Domlike */,
    destroyed: true,
    dropBackendAfterRelease: true,
};
/**
 * A general element
 *
 * An element can be a `NativeNode` , a `Component` , or a `VirtualNode` .
 */
export class Element {
    /* istanbul ignore next */
    constructor() {
        throw new Error('Element cannot be constructed directly');
    }
    /* @internal */
    _$initialize(is, virtual, backendElement, owner, nodeTreeContext) {
        this.is = is;
        this._$backendElement = backendElement;
        this._$destroyOnRemoval = 0 /* AutoDestroyState.Disabled */;
        this._$nodeTreeContext = nodeTreeContext;
        this._$nodeId = '';
        this._$nodeAttributes = null;
        this._$nodeSlot = '';
        this._$slotName = null;
        this._$slotElement = null;
        this._$slotValues = null;
        this._$subtreeSlotStart = null;
        this._$subtreeSlotEnd = null;
        this._$inheritSlots = false;
        this._$virtual = virtual;
        this.dataset = {};
        this._$marks = null;
        this._$attached = false;
        this.classList = null;
        this._$styleSegments = [];
        this.parentNode = null;
        this.parentIndex = -1;
        this.childNodes = [];
        this.slotNodes = undefined;
        this.ownerShadowRoot = owner;
        this._$mutationObserverTarget = null;
        this._$eventTarget = new EventTarget();
    }
    get $$() {
        return this._$backendElement;
    }
    get id() {
        return this._$nodeId;
    }
    set id(x) {
        var _a;
        const newId = String(x);
        if (this._$nodeId === newId)
            return;
        this._$nodeId = newId;
        const be = this._$backendElement;
        if (this.ownerShadowRoot) {
            const host = this.ownerShadowRoot.getHostNode();
            this.ownerShadowRoot._$markIdCacheDirty();
            if (host.getComponentOptions().writeIdToDOM) {
                const idPrefix = host._$idPrefix;
                const idWithPrefix = idPrefix ? `${idPrefix}--${newId}` : newId;
                if (be) {
                    if (BM.DOMLIKE || (BM.DYNAMIC && this.getBackendMode() === 3 /* BackendMode.Domlike */)) {
                        if (ENV.DEV)
                            performanceMeasureStart('backend.setId');
                        be.id = idWithPrefix;
                        if (ENV.DEV)
                            performanceMeasureEnd();
                    }
                    else if (BM.COMPOSED ||
                        (BM.DYNAMIC && this.getBackendMode() === 2 /* BackendMode.Composed */)) {
                        if (ENV.DEV)
                            performanceMeasureStart('backend.setId');
                        be.setId(idWithPrefix);
                        if (ENV.DEV)
                            performanceMeasureEnd();
                    }
                }
            }
        }
        if (BM.SHADOW || (BM.DYNAMIC && this.getBackendMode() === 1 /* BackendMode.Shadow */)) {
            if (ENV.DEV)
                performanceMeasureStart('backend.setId');
            be.setId(newId);
            if (ENV.DEV)
                performanceMeasureEnd();
        }
        if (globalOptions.writeExtraInfoToAttr) {
            (_a = this._$backendElement) === null || _a === void 0 ? void 0 : _a.setAttribute('exparser:info-attr-id', newId);
        }
        if (this._$mutationObserverTarget) {
            MutationObserverTarget.callAttrObservers(this, {
                type: 'properties',
                target: this,
                nameType: 'basic',
                attributeName: 'id',
            });
        }
    }
    get slot() {
        return this._$nodeSlot;
    }
    set slot(x) {
        const newSlot = String(x);
        const oldSlot = this._$nodeSlot;
        if (oldSlot === newSlot)
            return;
        /* istanbul ignore if  */
        if (this._$inheritSlots) {
            throw new Error('slots-inherited nodes do not support "slot" attribute.');
        }
        this._$nodeSlot = newSlot;
        if (BM.SHADOW || (BM.DYNAMIC && this.getBackendMode() === 1 /* BackendMode.Shadow */)) {
            ;
            this._$backendElement.setSlot(newSlot);
        }
        const slotParentShadowRoot = Element._$getParentHostShadowRoot(this.parentNode);
        if (slotParentShadowRoot) {
            const slotMode = slotParentShadowRoot.getSlotMode();
            /* istanbul ignore if  */
            if (slotMode === 3 /* SlotMode.Dynamic */) {
                throw new Error('nodes inside dynamic slots should change binding slots through Element#setSlotElement.');
            }
            const slotUpdater = Element._$updateSubtreeSlotNodes(this.parentNode, [this], slotParentShadowRoot, slotParentShadowRoot, this.parentIndex);
            slotUpdater === null || slotUpdater === void 0 ? void 0 : slotUpdater.removeSlotNodes();
            const oldSlot = this.containingSlot;
            slotUpdater === null || slotUpdater === void 0 ? void 0 : slotUpdater.updateContainingSlot();
            const newSlot = this.containingSlot;
            Element.insertChildReassign(this.parentNode, this, oldSlot, newSlot, this.parentIndex + 1);
            slotUpdater === null || slotUpdater === void 0 ? void 0 : slotUpdater.insertSlotNodes();
        }
        if (this._$mutationObserverTarget) {
            MutationObserverTarget.callAttrObservers(this, {
                type: 'properties',
                target: this,
                nameType: 'basic',
                attributeName: 'slot',
            });
        }
    }
    get attributes() {
        const ret = [];
        if (this._$nodeAttributes) {
            Object.entries(this._$nodeAttributes).forEach(([name, value]) => {
                ret.push({
                    name,
                    value,
                });
            });
        }
        return ret;
    }
    get class() {
        if (this.classList) {
            return this.classList.getClassNames(0 /* StyleSegmentIndex.MAIN */);
        }
        return '';
    }
    set class(classNames) {
        this.setNodeClass(classNames, 0 /* StyleSegmentIndex.MAIN */);
    }
    get style() {
        return this._$styleSegments[0 /* StyleSegmentIndex.MAIN */] || '';
    }
    set style(styleText) {
        this.setNodeStyle(styleText, 0 /* StyleSegmentIndex.MAIN */);
    }
    // eslint-disable-next-line class-methods-use-this
    asTextNode() {
        return null;
    }
    asElement() {
        return this;
    }
    asNativeNode() {
        if (isNativeNode(this)) {
            return this;
        }
        return null;
    }
    asVirtualNode() {
        if (isVirtualNode(this)) {
            return this;
        }
        return null;
    }
    asShadowRoot() {
        if (isShadowRoot(this)) {
            return this;
        }
        return null;
    }
    asGeneralComponent() {
        if (isComponent(this)) {
            return this;
        }
        return null;
    }
    asInstanceOf(componentDefinition) {
        if (isComponent(this)) {
            return this.asInstanceOf(componentDefinition);
        }
        return null;
    }
    /** Get the backend context */
    getBackendContext() {
        const context = this._$nodeTreeContext;
        if (BM.SHADOW) {
            if (context === DESTROYED_SHADOW_BACKEND_CONTEXT)
                return null;
        }
        else if (BM.COMPOSED) {
            if (context === DESTROYED_COMPOSED_BACKEND_CONTEXT)
                return null;
        }
        else if (BM.DOMLIKE) {
            if (context === DESTROYED_DOMLIKE_BACKEND_CONTEXT)
                return null;
        }
        else {
            if (context === DESTROYED_SHADOW_BACKEND_CONTEXT)
                return null;
            if (context === DESTROYED_COMPOSED_BACKEND_CONTEXT)
                return null;
            if (context === DESTROYED_DOMLIKE_BACKEND_CONTEXT)
                return null;
        }
        return context;
    }
    /** Get the backend mode */
    getBackendMode() {
        return this._$nodeTreeContext.mode;
    }
    /** Get the backend element */
    getBackendElement() {
        return this._$backendElement;
    }
    /**
     * Destroy the backend element
     *
     * It only destroy the backend element of the element itself.
     */
    destroyBackendElement() {
        if (this._$backendElement) {
            if (!(BM.DOMLIKE || (BM.DYNAMIC && this.getBackendMode() === 3 /* BackendMode.Domlike */))) {
                if (ENV.DEV)
                    performanceMeasureStart('backend.release');
                this._$backendElement.release();
                if (ENV.DEV)
                    performanceMeasureEnd();
            }
        }
        if (this._$nodeTreeContext.dropBackendAfterRelease) {
            this._$backendElement = null;
            if (BM.COMPOSED || (BM.DYNAMIC && this.getBackendMode() === 2 /* BackendMode.Composed */)) {
                this._$nodeTreeContext = DESTROYED_COMPOSED_BACKEND_CONTEXT;
            }
            else if (BM.DOMLIKE || (BM.DYNAMIC && this.getBackendMode() === 3 /* BackendMode.Domlike */)) {
                this._$nodeTreeContext = DESTROYED_DOMLIKE_BACKEND_CONTEXT;
            }
            else {
                this._$nodeTreeContext = DESTROYED_SHADOW_BACKEND_CONTEXT;
            }
        }
    }
    /**
     * Destroy backend element for the whole subtree.
     *
     * It will destroy backend elements for the whole subtree (shadow tree) recursively.
     * If a backend element for a component is destroyed,
     * any backend element in the shadow tree of the component will also be destroyed.
     */
    destroyBackendElementOnSubtree() {
        const rec = function (elem) {
            for (let i = 0; i < elem.childNodes.length; i += 1) {
                const node = elem.childNodes[i];
                if (isElement(node))
                    rec(node);
                else
                    node.destroyBackendElement();
            }
            if (isComponent(elem)) {
                const shadowRoot = elem.getShadowRoot();
                if (shadowRoot)
                    rec(shadowRoot);
            }
            elem.destroyBackendElement();
        };
        rec(this);
    }
    /**
     * Destroy the backend element when removed from any parent element
     */
    destroyBackendElementOnRemoval() {
        this._$destroyOnRemoval = 1 /* AutoDestroyState.Enabled */;
    }
    /**
     * Cancel the destroy scheduling of the backend element
     */
    cancelDestroyBackendElementOnRemoval() {
        this._$destroyOnRemoval = 0 /* AutoDestroyState.Disabled */;
    }
    /**
     * Destroy the backend element when removed from any parent element
     *
     * @deprecated Use `destroyBackendElementOnRemoval` instead.
     */
    destroyBackendElementOnDetach() {
        this._$destroyOnRemoval = 1 /* AutoDestroyState.Enabled */;
    }
    /**
     * Cancel the destroy scheduling of the backend element
     *
     * @deprecated Use `cancelDestroyBackendElementOnRemoval` instead.
     */
    cancelDestroyBackendElementOnDetach() {
        this._$destroyOnRemoval = 0 /* AutoDestroyState.Disabled */;
    }
    /** Get whether the node is virtual or not */
    isVirtual() {
        return this._$virtual;
    }
    setNodeClass(classNames, index = 0 /* StyleSegmentIndex.MAIN */) {
        var _a;
        if (ENV.DEV)
            performanceMeasureStart('backend.setClass');
        const changed = (_a = this.classList) === null || _a === void 0 ? void 0 : _a.setClassNames(classNames, index);
        if (ENV.DEV)
            performanceMeasureEnd();
        if (changed && this._$mutationObserverTarget) {
            MutationObserverTarget.callAttrObservers(this, {
                type: 'properties',
                target: this,
                nameType: 'basic',
                attributeName: 'class',
            });
        }
    }
    /** Toggle the node class */
    setNodeClassList(classNames, index = 0 /* StyleSegmentIndex.MAIN */) {
        var _a;
        if (ENV.DEV)
            performanceMeasureStart('backend.setClass');
        const changed = (_a = this.classList) === null || _a === void 0 ? void 0 : _a.setClassNameList(classNames, index);
        if (ENV.DEV)
            performanceMeasureEnd();
        if (changed && this._$mutationObserverTarget) {
            MutationObserverTarget.callAttrObservers(this, {
                type: 'properties',
                target: this,
                nameType: 'basic',
                attributeName: 'class',
            });
        }
    }
    /** Toggle the node class */
    toggleNodeClass(classNames, force, index = 0 /* StyleSegmentIndex.MAIN */) {
        var _a;
        if (ENV.DEV)
            performanceMeasureStart('backend.setClass');
        const changed = (_a = this.classList) === null || _a === void 0 ? void 0 : _a.toggle(classNames, force, index);
        if (ENV.DEV)
            performanceMeasureEnd();
        if (changed && this._$mutationObserverTarget) {
            MutationObserverTarget.callAttrObservers(this, {
                type: 'properties',
                target: this,
                nameType: 'basic',
                attributeName: 'class',
            });
        }
    }
    /** Set the node style */
    setNodeStyle(styleSegment, index = 0 /* StyleSegmentIndex.MAIN */) {
        if (index === 0 /* StyleSegmentIndex.MAIN */ && this._$styleSegments[index] === styleSegment)
            return;
        this._$styleSegments[index] = styleSegment;
        const style = this._$styleSegments.join(';');
        if (ENV.DEV)
            performanceMeasureStart('backend.setStyle');
        if (this._$backendElement) {
            if (BM.DOMLIKE || (BM.DYNAMIC && this.getBackendMode() === 3 /* BackendMode.Domlike */)) {
                ;
                this._$backendElement.setAttribute('style', style);
            }
            else if (BM.COMPOSED || (BM.DYNAMIC && this.getBackendMode() === 2 /* BackendMode.Composed */)) {
                ;
                this._$backendElement.setStyle(style);
            }
            else {
                ;
                this._$backendElement.setStyle(styleSegment, index);
            }
        }
        if (ENV.DEV)
            performanceMeasureEnd();
        if (this._$mutationObserverTarget) {
            MutationObserverTarget.callAttrObservers(this, {
                type: 'properties',
                target: this,
                nameType: 'basic',
                attributeName: 'style',
            });
        }
    }
    getNodeStyleSegments() {
        return this._$styleSegments;
    }
    static checkAndCallAttached(node) {
        const callFunc = function callFunc(node) {
            if (isElement(node) && !node._$attached) {
                node._$attached = true;
                const nodeIsComponent = isComponent(node);
                if (nodeIsComponent) {
                    node.triggerLifetime('attached', []);
                    if (node._$relation) {
                        node._$relation.triggerLinkEvent(2 /* RelationType.ParentNonVirtualNode */, false);
                        node._$relation.triggerLinkEvent(4 /* RelationType.ParentComponent */, false);
                        node._$relation.triggerLinkEvent(0 /* RelationType.Ancestor */, false);
                    }
                }
                if (node._$mutationObserverTarget) {
                    MutationObserverTarget.callAttachObservers(node, {
                        type: 'attachStatus',
                        target: node,
                        status: 'attached',
                    });
                }
                if (nodeIsComponent) {
                    const shadowRoot = node.getShadowRoot();
                    if (shadowRoot)
                        callFunc(shadowRoot);
                }
                const childNodes = node.childNodes;
                for (let i = 0; i < childNodes.length; i += 1) {
                    callFunc(childNodes[i]);
                }
            }
        };
        callFunc(node);
    }
    static checkAndCallDetached(node) {
        // check and call detached lifetime recursively
        const callFunc = function callFunc(node) {
            if (isElement(node) && node._$attached) {
                if (isComponent(node)) {
                    node.triggerLifetime('beforeDetach', []);
                }
                node.childNodes.forEach(callFunc);
                if (isComponent(node)) {
                    const shadowRoot = node.getShadowRoot();
                    if (shadowRoot)
                        callFunc(shadowRoot);
                    node._$attached = false;
                    node.triggerLifetime('detached', []);
                    if (node._$relation) {
                        node._$relation.triggerLinkEvent(2 /* RelationType.ParentNonVirtualNode */, true);
                        node._$relation.triggerLinkEvent(4 /* RelationType.ParentComponent */, true);
                        node._$relation.triggerLinkEvent(0 /* RelationType.Ancestor */, true);
                    }
                }
                else {
                    node._$attached = false;
                }
                if (node._$mutationObserverTarget) {
                    MutationObserverTarget.callAttachObservers(node, {
                        type: 'attachStatus',
                        target: node,
                        status: 'detached',
                    });
                }
            }
        };
        callFunc(node);
        // check destroy on removal recursively
        if (isElement(node)) {
            const rec = function (elem) {
                if (node._$destroyOnRemoval === 2 /* AutoDestroyState.Destroyed */)
                    return;
                for (let i = 0; i < elem.childNodes.length; i += 1) {
                    const node = elem.childNodes[i];
                    if (isElement(node)) {
                        rec(node);
                    }
                    else if (node._$destroyOnRemoval === 1 /* AutoDestroyState.Enabled */) {
                        node._$destroyOnRemoval = 2 /* AutoDestroyState.Destroyed */;
                        node.destroyBackendElement();
                    }
                }
                if (isComponent(elem)) {
                    const shadowRoot = elem.getShadowRoot();
                    if (shadowRoot)
                        rec(shadowRoot);
                }
                if (elem._$destroyOnRemoval === 1 /* AutoDestroyState.Enabled */) {
                    elem._$destroyOnRemoval = 2 /* AutoDestroyState.Destroyed */;
                    elem.destroyBackendElement();
                }
            };
            rec(node);
        }
        else if (node._$destroyOnRemoval === 1 /* AutoDestroyState.Enabled */) {
            node._$destroyOnRemoval = 2 /* AutoDestroyState.Destroyed */;
            node.destroyBackendElement();
        }
    }
    static checkAndCallMoved(node) {
        const callFunc = function callFunc(node) {
            if (isElement(node) && node._$attached) {
                node.childNodes.forEach(callFunc);
                if (isComponent(node)) {
                    const shadowRoot = node.getShadowRoot();
                    if (shadowRoot)
                        callFunc(shadowRoot);
                    node.triggerLifetime('moved', []);
                    if (node._$relation) {
                        node._$relation.triggerLinkEvent(2 /* RelationType.ParentNonVirtualNode */, false);
                        node._$relation.triggerLinkEvent(4 /* RelationType.ParentComponent */, false);
                        node._$relation.triggerLinkEvent(0 /* RelationType.Ancestor */, false);
                    }
                }
            }
        };
        callFunc(node);
    }
    static checkChildObservers(node, type, child) {
        var _a;
        const observer = node._$mutationObserverTarget;
        if (observer && (!((_a = observer.childObservers) === null || _a === void 0 ? void 0 : _a.empty) || observer.hasSubtreeListeners())) {
            const childList = [child];
            let childEventObj;
            if (type === 'add') {
                childEventObj = {
                    type: 'childList',
                    target: node,
                    addedNodes: childList,
                };
            }
            else if (type === 'remove') {
                childEventObj = {
                    type: 'childList',
                    target: node,
                    removedNodes: childList,
                };
            }
            else {
                childEventObj = {
                    type: 'childList',
                    target: node,
                    addedNodes: childList,
                    removedNodes: childList,
                };
            }
            MutationObserverTarget.callChildObservers(node, childEventObj);
        }
    }
    /**
     * Get whether a node has any subtree `MutationObserver` attached to it
     *
     * If there is, then tree update may have more performance impact.
     */
    static hasSubtreeMutationObservers(node) {
        var _a;
        return ((_a = node._$mutationObserverTarget) === null || _a === void 0 ? void 0 : _a.hasSubtreeListeners()) || false;
    }
    /** @internal */
    static _$insertChildReassignSlot(shadowRoot, name, oldSlot, newSlot) {
        if (!oldSlot) {
            const slotNodes = [];
            shadowRoot.forEachNodeInSpecifiedSlot(null, (node) => {
                if (name !== null) {
                    const slotName = isElement(node) ? node._$nodeSlot : '';
                    if (slotName !== name) {
                        return;
                    }
                }
                slotNodes.push(node);
            });
            for (let i = 0; i < slotNodes.length; i += 1) {
                const node = slotNodes[i];
                Element._$spliceSlotNodes(newSlot, -1, 0, [node]);
                Element._$updateContainingSlot(node, newSlot);
                if (!node._$inheritSlots)
                    Element.insertChildReassign(node.parentNode, node, null, newSlot, node.parentIndex);
            }
        }
        else if (!newSlot) {
            const slotNodes = [...oldSlot.slotNodes];
            for (let i = 0; i < slotNodes.length; i += 1) {
                const node = slotNodes[i];
                Element._$spliceSlotNodes(oldSlot, 0, 1, undefined);
                Element._$updateContainingSlot(node, newSlot);
                if (!node._$inheritSlots)
                    Element.insertChildReassign(node.parentNode, node, oldSlot, null, node.parentIndex);
            }
        }
        else {
            const slotNodes = [...oldSlot.slotNodes];
            for (let i = 0; i < slotNodes.length; i += 1) {
                const node = slotNodes[i];
                Element._$spliceSlotNodes(oldSlot, 0, 1, undefined);
                Element._$spliceSlotNodes(newSlot, -1, 0, [node]);
                Element._$updateContainingSlot(node, newSlot);
                if (!node._$inheritSlots)
                    Element.insertChildReassign(node.parentNode, node, oldSlot, newSlot, node.parentIndex);
            }
        }
    }
    static insertChildReassign(shadowParent, child, oldSlot, newSlot, ideaPosIndex) {
        if (BM.SHADOW || (BM.DYNAMIC && shadowParent.getBackendMode() === 1 /* BackendMode.Shadow */)) {
            return;
        }
        if (oldSlot) {
            if (newSlot &&
                (BM.DOMLIKE || (BM.DYNAMIC && shadowParent.getBackendMode() === 3 /* BackendMode.Domlike */))) {
                // removal of in-tree elements are not needed for DOM backend
                // do nothing
            }
            else {
                const backendParent = Element.findNearestNonVirtual(oldSlot);
                if (backendParent) {
                    const d = Element.countNonVirtual(child);
                    if (d) {
                        const [before, removeCount] = d;
                        if (BM.DOMLIKE ||
                            (BM.DYNAMIC && shadowParent.getBackendMode() === 3 /* BackendMode.Domlike */)) {
                            const rel = before._$backendElement;
                            for (let i = 1; i < removeCount; i += 1) {
                                const next = rel.nextSibling;
                                if (next) {
                                    if (ENV.DEV)
                                        performanceMeasureStart('backend.removeChild');
                                    backendParent.removeChild(next);
                                    if (ENV.DEV)
                                        performanceMeasureEnd();
                                }
                            }
                            if (removeCount > 0) {
                                if (ENV.DEV)
                                    performanceMeasureStart('backend.removeChild');
                                backendParent.removeChild(rel);
                                if (ENV.DEV)
                                    performanceMeasureEnd();
                            }
                        }
                        else {
                            if (ENV.DEV)
                                performanceMeasureStart('backend.spliceRemove');
                            backendParent.spliceRemove(before._$backendElement, removeCount);
                            if (ENV.DEV)
                                performanceMeasureEnd();
                        }
                    }
                }
            }
        }
        if (newSlot) {
            Element.insertChildComposed(shadowParent, child, undefined, false, ideaPosIndex);
        }
    }
    // a helper for searching the nearest non-virtual ancestor
    static findNearestNonVirtual(p) {
        let cur = p;
        for (; cur === null || cur === void 0 ? void 0 : cur._$virtual;) {
            if (isShadowRoot(cur)) {
                cur = cur.getHostNode();
                continue;
            }
            if (cur.containingSlot !== undefined) {
                cur = cur.containingSlot;
                continue;
            }
            cur = cur.parentNode;
        }
        if (isComponent(cur) && cur._$external) {
            return cur.shadowRoot.slot;
        }
        return cur ? cur._$backendElement : null;
    }
    static countNonVirtual(target) {
        let firstNode = null;
        let removeCount = 0;
        const recNonVirtual = (c) => {
            if (removeCount === 0)
                firstNode = c;
            removeCount += 1;
        };
        const rec = (c) => {
            if (isElement(c) && c._$virtual) {
                c.forEachNonVirtualComposedChild(recNonVirtual);
                return;
            }
            recNonVirtual(c);
        };
        rec(target);
        return firstNode ? [firstNode, removeCount] : null;
    }
    /**
     * Iterate elements with their slots (slots-inherited nodes included)
     */
    static forEachNodeInSlot(node, f) {
        const rec = (child) => {
            if (f(child, child.containingSlot) === false)
                return false;
            if (child._$inheritSlots) {
                const childNodes = child.childNodes;
                for (let i = 0; i < childNodes.length; i += 1) {
                    if (!rec(childNodes[i]))
                        return false;
                }
                return true;
            }
            return true;
        };
        return rec(node);
    }
    /**
     * Iterate elements in specified slot (slots-inherited nodes included)
     */
    static forEachNodeInSpecificSlot(node, slot, f) {
        const rec = (child) => {
            if (child.containingSlot === slot) {
                if (f(child) === false)
                    return false;
            }
            if (child._$inheritSlots) {
                const childNodes = child.childNodes;
                for (let i = 0; i < childNodes.length; i += 1) {
                    if (!rec(childNodes[i]))
                        return false;
                }
                return true;
            }
            return true;
        };
        return rec(node);
    }
    /**
     * Iterate elements with their slots (slots-inherited nodes NOT included)
     */
    static forEachSlotContentInSlot(node, f) {
        const rec = (child) => {
            if (child._$inheritSlots) {
                const childNodes = child.childNodes;
                for (let i = 0; i < childNodes.length; i += 1) {
                    if (!rec(childNodes[i]))
                        return false;
                }
                return true;
            }
            if (f(child, child.containingSlot) === false)
                return false;
            return true;
        };
        return rec(node);
    }
    /**
     * Iterate elements in specified slot (slots-inherited nodes NOT included)
     */
    static forEachSlotContentInSpecificSlot(node, slot, f) {
        const rec = (child) => {
            if (child._$inheritSlots) {
                const childNodes = child.childNodes;
                for (let i = 0; i < childNodes.length; i += 1) {
                    if (!rec(childNodes[i]))
                        return false;
                }
                return true;
            }
            if (child.containingSlot !== slot)
                return true;
            return f(child) !== false;
        };
        return rec(node);
    }
    /**
     * @internal
     * a helper for searching the first non-virtual node
     */
    static _$findFirstNonVirtualChild(parent, index) {
        const inSlot = parent._$slotName !== null;
        const children = inSlot ? parent.slotNodes : parent.childNodes;
        if (index >= 0 && index < children.length) {
            for (let i = index; i < children.length; i += 1) {
                let ret = null;
                const recNonVirtual = (c) => {
                    ret = c;
                    return false;
                };
                const rec = (c) => {
                    if (!inSlot && c._$inheritSlots) {
                        const childNodes = c.childNodes;
                        for (let i = 0; i < childNodes.length; i += 1) {
                            if (rec(childNodes[i]) === false)
                                return false;
                        }
                    }
                    if (c._$virtual) {
                        return c.forEachNonVirtualComposedChild(recNonVirtual);
                    }
                    return recNonVirtual(c);
                };
                rec(children[i]);
                if (ret)
                    return ret;
            }
        }
        const recvParent = (parent) => {
            if (!parent._$virtual)
                return null;
            const containingSlot = parent.containingSlot;
            if (containingSlot === null)
                return null;
            if (containingSlot !== undefined) {
                return Element._$findFirstNonVirtualChild(containingSlot, parent.slotIndex + 1);
            }
            if (isShadowRoot(parent)) {
                return recvParent(parent.getHostNode());
            }
            const p = parent.parentNode;
            if (p) {
                return Element._$findFirstNonVirtualChild(p, parent.parentIndex + 1);
            }
            return null;
        };
        return recvParent(parent);
    }
    /**
     * @internal
     * A helper to find first non-virtual next sibling node
     * return null if no next sibling exists
     */
    static _$findFirstNonVirtualSibling(element, newPosIndex) {
        const containingSlot = element.containingSlot;
        if (containingSlot === null)
            return null;
        if (containingSlot !== undefined) {
            if (!containingSlot._$virtual)
                return null;
            if (element.slotIndex !== undefined) {
                return Element._$findFirstNonVirtualChild(containingSlot, element.slotIndex + 1);
            }
            const insertPos = Element._$findSlotNodeInsertPosition(containingSlot, element, newPosIndex);
            return Element._$findFirstNonVirtualChild(containingSlot, insertPos);
        }
        let cur = element;
        if (isShadowRoot(element)) {
            cur = element.getHostNode();
            if (!cur._$virtual)
                return null;
            return Element._$findFirstNonVirtualChild(cur, cur.parentIndex + 1);
        }
        const p = cur.parentNode;
        if (p) {
            return Element._$findFirstNonVirtualChild(p, newPosIndex);
        }
        return null;
    }
    static insertChildComposed(shadowParent, newChild, relChild, removal, newPosIndex) {
        var _a, _b, _c;
        let parentConverted = false;
        let cur = shadowParent;
        while (cur === null || cur === void 0 ? void 0 : cur._$inheritSlots) {
            parentConverted = true;
            cur = cur.parentNode;
        }
        if (!cur)
            return;
        const slotParent = cur;
        const context = slotParent._$nodeTreeContext;
        if (context.destroyed)
            return;
        // detect whether it is in single-slot mode
        let sharedNonVirtualParent;
        if (isComponent(slotParent)) {
            if (slotParent._$external) {
                sharedNonVirtualParent = slotParent.shadowRoot
                    .slot;
            }
            else {
                parentConverted = true;
            }
        }
        else {
            if (slotParent._$virtual)
                parentConverted = true;
            sharedNonVirtualParent = Element.findNearestNonVirtual(slotParent);
        }
        // a helper for grouping and slicing update
        let sharedFrag = null;
        const groupUpdate = (sharedBackendParent, slot) => {
            const backendParent = sharedBackendParent || (slot ? Element.findNearestNonVirtual(slot) : null);
            if (!(BM.DOMLIKE || (BM.DYNAMIC && shadowParent.getBackendMode() === 3 /* BackendMode.Domlike */))) {
                if (!backendParent) {
                    return;
                }
            }
            let removeCount = 0;
            let before = null;
            // get the new child nodes of the backend
            let frag = null;
            let firstSlotNode = null;
            if (newChild) {
                const f = sharedFrag ||
                    (BM.DOMLIKE || (BM.DYNAMIC && context.mode === 3 /* BackendMode.Domlike */)
                        ? context.document.createDocumentFragment()
                        : context.createFragment());
                sharedFrag = f;
                const recNonVirtual = (c) => {
                    if (ENV.DEV)
                        performanceMeasureStart('backend.appendChild');
                    f.appendChild(c._$backendElement);
                    if (ENV.DEV)
                        performanceMeasureEnd();
                    frag = f;
                };
                Element.forEachNodeInSpecificSlot(newChild, slot, (node) => {
                    if (!firstSlotNode)
                        firstSlotNode = node;
                    if (!node._$virtual) {
                        recNonVirtual(node);
                        return;
                    }
                    node.forEachNonVirtualComposedChild(recNonVirtual);
                });
            }
            if (BM.DOMLIKE || (BM.DYNAMIC && shadowParent.getBackendMode() === 3 /* BackendMode.Domlike */)) {
                if (!backendParent) {
                    sharedFrag = null;
                    return;
                }
            }
            // get the proper relative node of the backend
            if (relChild || parentConverted) {
                if (removal && relChild) {
                    Element.forEachSlotContentInSpecificSlot(relChild, slot, (c) => {
                        if (isElement(c)) {
                            const d = Element.countNonVirtual(c);
                            if (d) {
                                if (!before)
                                    before = d[0];
                                removeCount += d[1];
                            }
                        }
                        else {
                            if (!before)
                                before = c;
                            removeCount += 1;
                        }
                        return true;
                    });
                    if (removeCount === 0 && frag && firstSlotNode) {
                        before = Element._$findFirstNonVirtualSibling(slot === undefined ? newChild : firstSlotNode, newPosIndex + 1);
                    }
                }
                else if (frag && firstSlotNode) {
                    before = Element._$findFirstNonVirtualSibling(slot === undefined ? newChild : firstSlotNode, newPosIndex);
                }
            }
            // actually do the backend operation
            if (frag) {
                if (before) {
                    if (BM.DOMLIKE || (BM.DYNAMIC && context.mode === 3 /* BackendMode.Domlike */)) {
                        const rel = before._$backendElement;
                        if (ENV.DEV)
                            performanceMeasureStart('backend.insertBefore');
                        backendParent.insertBefore(frag, rel);
                        if (ENV.DEV)
                            performanceMeasureEnd();
                        for (let i = 1; i < removeCount; i += 1) {
                            const next = rel.nextSibling;
                            if (next) {
                                if (ENV.DEV)
                                    performanceMeasureStart('backend.removeChild');
                                backendParent.removeChild(next);
                                if (ENV.DEV)
                                    performanceMeasureEnd();
                            }
                        }
                        if (removeCount > 0) {
                            if (ENV.DEV)
                                performanceMeasureStart('backend.removeChild');
                            backendParent.removeChild(rel);
                            if (ENV.DEV)
                                performanceMeasureEnd();
                        }
                    }
                    else {
                        if (ENV.DEV)
                            performanceMeasureStart('backend.spliceBefore');
                        backendParent.spliceBefore(before._$backendElement, removeCount, frag);
                        if (ENV.DEV)
                            performanceMeasureEnd();
                    }
                }
                else {
                    if (BM.DOMLIKE || (BM.DYNAMIC && context.mode === 3 /* BackendMode.Domlike */)) {
                        if (ENV.DEV)
                            performanceMeasureStart('backend.appendChild');
                        backendParent.appendChild(frag);
                        if (ENV.DEV)
                            performanceMeasureEnd();
                    }
                    else {
                        if (ENV.DEV)
                            performanceMeasureStart('backend.spliceAppend');
                        backendParent.spliceAppend(frag);
                        if (ENV.DEV)
                            performanceMeasureEnd();
                    }
                }
            }
            else if (removeCount > 0) {
                if (BM.DOMLIKE || (BM.DYNAMIC && context.mode === 3 /* BackendMode.Domlike */)) {
                    const rel = before._$backendElement;
                    for (let i = 1; i < removeCount; i += 1) {
                        const next = rel.nextSibling;
                        if (next) {
                            if (ENV.DEV)
                                performanceMeasureStart('backend.removeChild');
                            backendParent.removeChild(next);
                            if (ENV.DEV)
                                performanceMeasureEnd();
                        }
                    }
                    if (removeCount > 0) {
                        if (ENV.DEV)
                            performanceMeasureStart('backend.removeChild');
                        backendParent.removeChild(rel);
                        if (ENV.DEV)
                            performanceMeasureEnd();
                    }
                }
                else {
                    if (ENV.DEV)
                        performanceMeasureStart('backend.spliceRemove');
                    backendParent.spliceRemove(before._$backendElement, removeCount);
                    if (ENV.DEV)
                        performanceMeasureEnd();
                }
            }
        };
        // in single-slot mode, use a simpler update logic; otherwise use a slower one
        if (sharedNonVirtualParent !== undefined) {
            if (sharedNonVirtualParent === null) {
                // for nodes with no valid non-virtual parent, do nothing
            }
            else if (!parentConverted &&
                (!isElement(newChild) || !newChild._$virtual) &&
                (!isElement(relChild) || !relChild._$virtual)) {
                // for non-virtual children, use single child operation
                if (removal) {
                    if (newChild) {
                        if (ENV.DEV)
                            performanceMeasureStart('backend.replaceChild');
                        sharedNonVirtualParent.replaceChild(newChild._$backendElement, relChild._$backendElement);
                        if (ENV.DEV)
                            performanceMeasureEnd();
                    }
                    else {
                        if (ENV.DEV)
                            performanceMeasureStart('backend.removeChild');
                        sharedNonVirtualParent.removeChild(relChild._$backendElement);
                        if (ENV.DEV)
                            performanceMeasureEnd();
                    }
                }
                else if (relChild) {
                    if (ENV.DEV)
                        performanceMeasureStart('backend.insertBefore');
                    sharedNonVirtualParent.insertBefore(newChild._$backendElement, relChild._$backendElement);
                    if (ENV.DEV)
                        performanceMeasureEnd();
                }
                else {
                    if (ENV.DEV)
                        performanceMeasureStart('backend.appendChild');
                    sharedNonVirtualParent.appendChild(newChild._$backendElement);
                    if (ENV.DEV)
                        performanceMeasureEnd();
                }
            }
            else {
                // for node isn't a slot content, simply update
                groupUpdate(sharedNonVirtualParent, undefined);
            }
        }
        else if (!((newChild && newChild._$inheritSlots) || (removal && relChild && relChild._$inheritSlots))) {
            // faster update if none inherited slots
            if (newChild)
                groupUpdate(null, newChild.containingSlot);
            if (removal &&
                relChild &&
                (!newChild || relChild.containingSlot !== newChild.containingSlot)) {
                groupUpdate(null, relChild.containingSlot);
            }
        }
        else {
            // for multi-slots, find out each slot that needs update, and update them one by one
            const slotNodesSet = new Set();
            if (removal && relChild) {
                Element.forEachSlotContentInSlot(relChild, (node, slot) => {
                    slotNodesSet.add(slot);
                });
            }
            if (newChild) {
                Element.forEachSlotContentInSlot(newChild, (node, slot) => {
                    slotNodesSet.add(slot);
                });
            }
            slotNodesSet.forEach((slot) => groupUpdate(null, slot));
        }
        if (!(BM.DOMLIKE || (BM.DYNAMIC && context.mode === 3 /* BackendMode.Domlike */))) {
            if (sharedFrag) {
                if (ENV.DEV)
                    performanceMeasureStart('backend.release');
                sharedFrag.release();
                if (ENV.DEV)
                    performanceMeasureEnd();
            }
        }
        // write extra info if needed
        if (globalOptions.writeExtraInfoToAttr) {
            if (removal && isElement(relChild)) {
                (_a = relChild._$backendElement) === null || _a === void 0 ? void 0 : _a.removeAttribute('exparser:info-in-slot-of');
            }
            if (isElement(newChild)) {
                if (isComponent(shadowParent)) {
                    (_b = newChild._$backendElement) === null || _b === void 0 ? void 0 : _b.setAttribute('exparser:info-in-slot-of', shadowParent._$componentInstanceId);
                }
                else {
                    (_c = newChild._$backendElement) === null || _c === void 0 ? void 0 : _c.removeAttribute('exparser:info-in-slot-of');
                }
            }
        }
    }
    /**
     * @internal
     * @param move whether this insertion is a slot movement
     */
    static _$updateSubtreeSlotsInsertion(node, slotStart, slotEnd, posIndex, move) {
        if (!slotStart || !slotEnd)
            return;
        let parent = node;
        // find a correct position to insert slot into double-linked slot list
        let insertSlotPrev = null;
        let insertSlotNext = null;
        const findFirstSlot = (parent, posIndex) => {
            if (parent._$subtreeSlotStart) {
                const childNodes = parent.childNodes;
                let index = posIndex + 1;
                let nextSiblingWithSlot = childNodes[index];
                while (nextSiblingWithSlot && !nextSiblingWithSlot._$subtreeSlotStart) {
                    index += 1;
                    nextSiblingWithSlot = childNodes[index];
                }
                if (nextSiblingWithSlot) {
                    insertSlotNext = nextSiblingWithSlot._$subtreeSlotStart;
                    insertSlotPrev = insertSlotNext.prev;
                }
                else {
                    insertSlotPrev = parent._$subtreeSlotEnd;
                    if (insertSlotPrev)
                        insertSlotNext = insertSlotPrev.next;
                }
                return;
            }
            let cur = parent;
            let parentWithSubtreeSlot = parent.parentNode;
            while (parentWithSubtreeSlot && !parentWithSubtreeSlot._$subtreeSlotStart) {
                cur = parentWithSubtreeSlot;
                parentWithSubtreeSlot = parentWithSubtreeSlot.parentNode;
            }
            if (parentWithSubtreeSlot) {
                findFirstSlot(parentWithSubtreeSlot, cur.parentIndex);
            }
        };
        findFirstSlot(parent, posIndex);
        // insert into double linked list
        if (insertSlotPrev) {
            insertSlotPrev.next = slotStart;
            slotStart.prev = insertSlotPrev;
        }
        if (insertSlotNext) {
            insertSlotNext.prev = slotEnd;
            slotEnd.next = insertSlotNext;
        }
        // update ancestor's subtree slot start/end
        while (parent) {
            let changed = false;
            if (!parent._$subtreeSlotStart) {
                parent._$subtreeSlotStart = slotStart;
                parent._$subtreeSlotEnd = slotEnd;
                changed = true;
            }
            else if (parent._$subtreeSlotStart === insertSlotNext) {
                parent._$subtreeSlotStart = slotStart;
                changed = true;
            }
            else if (parent._$subtreeSlotEnd === insertSlotPrev) {
                parent._$subtreeSlotEnd = slotEnd;
                changed = true;
            }
            if (!changed || !parent.parentNode)
                break;
            parent = parent.parentNode;
        }
        const ownerShadowRoot = parent.ownerShadowRoot;
        if (ownerShadowRoot === null || ownerShadowRoot === void 0 ? void 0 : ownerShadowRoot.isConnected(parent)) {
            ownerShadowRoot._$applySlotsInsertion(slotStart, slotEnd, move);
        }
    }
    /**
     * @internal
     * @param move whether this removal is a slot movement, will fire an insertion (with move=true) after
     */
    static _$updateSubtreeSlotsRemoval(node, slotStart, slotEnd, move) {
        if (!slotStart || !slotEnd)
            return;
        let parent = node;
        const removeSlotBefore = slotStart.prev;
        const removeSlotAfter = slotEnd.next;
        // remove from double linked list
        if (removeSlotBefore) {
            removeSlotBefore.next = removeSlotAfter;
            slotStart.prev = null;
        }
        if (removeSlotAfter) {
            removeSlotAfter.prev = removeSlotBefore;
            slotEnd.next = null;
        }
        // update parent subtree start/end
        while (parent) {
            let changed = false;
            if (parent._$subtreeSlotStart === slotStart && parent._$subtreeSlotEnd === slotEnd) {
                parent._$subtreeSlotStart = parent._$subtreeSlotEnd = null;
                changed = true;
            }
            if (parent._$subtreeSlotStart === slotStart) {
                parent._$subtreeSlotStart = removeSlotAfter;
                changed = true;
            }
            else if (parent._$subtreeSlotEnd === slotEnd) {
                parent._$subtreeSlotEnd = removeSlotBefore;
                changed = true;
            }
            if (!changed || !parent.parentNode)
                break;
            parent = parent.parentNode;
        }
        const ownerShadowRoot = parent.ownerShadowRoot;
        if (ownerShadowRoot === null || ownerShadowRoot === void 0 ? void 0 : ownerShadowRoot.isConnected(parent)) {
            ownerShadowRoot._$applySlotsRemoval(slotStart, slotEnd, move);
        }
    }
    /**
     * @internal
     * @param move whether this removal is a slot movement, will fire an insertion (with move=true) after
     */
    static _$updateSubtreeSlotsReplacement(node, slotStart, slotEnd, oldSlotStart, oldSlotEnd, posIndex, move) {
        if (!slotStart || !slotEnd) {
            Element._$updateSubtreeSlotsRemoval(node, oldSlotStart, oldSlotEnd, move);
            return;
        }
        if (!oldSlotStart || !oldSlotEnd) {
            Element._$updateSubtreeSlotsInsertion(node, slotStart, slotEnd, posIndex, move);
            return;
        }
        let parent = node;
        const removeSlotBefore = oldSlotStart.prev;
        const removeSlotAfter = oldSlotEnd.next;
        // replace in double linked list
        if (removeSlotBefore) {
            removeSlotBefore.next = slotStart;
            slotStart.prev = removeSlotBefore;
            oldSlotStart.prev = null;
        }
        if (removeSlotAfter) {
            removeSlotAfter.prev = slotEnd;
            slotEnd.next = removeSlotAfter;
            oldSlotEnd.next = null;
        }
        // update parent subtree start/end
        while (parent) {
            let changed = false;
            if (parent._$subtreeSlotStart === oldSlotStart) {
                parent._$subtreeSlotStart = slotStart;
                changed = true;
            }
            if (parent._$subtreeSlotEnd === oldSlotEnd) {
                parent._$subtreeSlotEnd = slotEnd;
                changed = true;
            }
            if (!changed || !parent.parentNode)
                break;
            parent = parent.parentNode;
        }
        const ownerShadowRoot = parent.ownerShadowRoot;
        if (ownerShadowRoot === null || ownerShadowRoot === void 0 ? void 0 : ownerShadowRoot.isConnected(parent)) {
            ownerShadowRoot._$applySlotsRemoval(oldSlotStart, oldSlotEnd, false);
            ownerShadowRoot._$applySlotsInsertion(slotStart, slotEnd, move);
        }
    }
    /** @internal */
    static _$updateSubtreeSlotNodes(parentNode, elements, shadowRoot, oldShadowRoot, posIndex) {
        if (!shadowRoot && !oldShadowRoot)
            return undefined;
        const slotMode = shadowRoot === null || shadowRoot === void 0 ? void 0 : shadowRoot.getSlotMode();
        const oldSlotMode = oldShadowRoot === null || oldShadowRoot === void 0 ? void 0 : oldShadowRoot.getSlotMode();
        if ((slotMode === undefined || slotMode === 1 /* SlotMode.Single */) &&
            (oldSlotMode === undefined || oldSlotMode === 1 /* SlotMode.Single */)) {
            let removeStart = -1;
            let removeCount = 0;
            let insertPos = -1;
            const slotNodesToUpdate = [];
            const oldContainingSlot = elements[0].containingSlot;
            const containingSlot = shadowRoot === null || shadowRoot === void 0 ? void 0 : shadowRoot.getContainingSlot(elements[0]);
            for (let i = 0; i < elements.length; i += 1) {
                const elem = elements[i];
                // eslint-disable-next-line no-loop-func
                Element.forEachNodeInSlot(elem, (node) => {
                    if (oldContainingSlot) {
                        if (removeCount) {
                            removeCount += 1;
                        }
                        else {
                            removeStart = node.slotIndex;
                            removeCount = 1;
                        }
                    }
                    slotNodesToUpdate.push(node);
                });
            }
            if (containingSlot && slotNodesToUpdate.length) {
                const firstSlotNode = slotNodesToUpdate[0];
                insertPos = Element._$findSlotNodeInsertPosition(containingSlot, firstSlotNode, posIndex);
            }
            return {
                updateContainingSlot: () => {
                    for (let i = slotNodesToUpdate.length - 1; i >= 0; i -= 1) {
                        const node = slotNodesToUpdate[i];
                        Element._$updateContainingSlot(node, containingSlot);
                    }
                },
                removeSlotNodes: () => {
                    if (oldShadowRoot && oldContainingSlot && removeCount) {
                        Element._$spliceSlotNodes(oldContainingSlot, removeStart, removeCount, undefined);
                    }
                },
                insertSlotNodes: () => {
                    if (shadowRoot && containingSlot && slotNodesToUpdate.length) {
                        Element._$spliceSlotNodes(containingSlot, insertPos, 0, slotNodesToUpdate);
                    }
                },
            };
        }
        const slotNodesToInsertMap = new Map();
        const slotNodesToRemoveMap = new Map();
        const slotNodesWithContainingSlot = [];
        for (let i = 0; i < elements.length; i += 1) {
            const elem = elements[i];
            Element.forEachNodeInSlot(elem, (node, oldContainingSlot) => {
                const containingSlot = shadowRoot === null || shadowRoot === void 0 ? void 0 : shadowRoot.getContainingSlot(node);
                if (oldContainingSlot) {
                    const slotNodesToRemove = slotNodesToRemoveMap.get(oldContainingSlot);
                    if (slotNodesToRemove) {
                        slotNodesToRemove.count += 1;
                    }
                    else {
                        slotNodesToRemoveMap.set(oldContainingSlot, { start: node.slotIndex, count: 1 });
                    }
                }
                if (containingSlot) {
                    const slotNodesToInsert = slotNodesToInsertMap.get(containingSlot);
                    if (slotNodesToInsert) {
                        slotNodesToInsert.nodes.push(node);
                    }
                    else {
                        slotNodesToInsertMap.set(containingSlot, { nodes: [node], insertPos: -1 });
                    }
                }
                slotNodesWithContainingSlot.push([node, containingSlot]);
            });
        }
        if (shadowRoot && slotNodesToInsertMap.size) {
            const iter = slotNodesToInsertMap.entries();
            for (let it = iter.next(); !it.done; it = iter.next()) {
                const [slot, { nodes: slotNodesToInsert }] = it.value;
                const firstSlotNodeToInsert = slotNodesToInsert[0];
                it.value[1].insertPos = Element._$findSlotNodeInsertPosition(slot, firstSlotNodeToInsert, posIndex);
            }
        }
        return {
            updateContainingSlot: () => {
                for (let i = slotNodesWithContainingSlot.length - 1; i >= 0; i -= 1) {
                    const [node, containingSlot] = slotNodesWithContainingSlot[i];
                    Element._$updateContainingSlot(node, containingSlot);
                }
            },
            removeSlotNodes: () => {
                if (oldShadowRoot && slotNodesToRemoveMap.size) {
                    const iter = slotNodesToRemoveMap.entries();
                    for (let it = iter.next(); !it.done; it = iter.next()) {
                        const [slot, { start, count }] = it.value;
                        Element._$spliceSlotNodes(slot, start, count, undefined);
                    }
                }
            },
            insertSlotNodes: () => {
                if (shadowRoot && slotNodesToInsertMap.size) {
                    const iter = slotNodesToInsertMap.entries();
                    for (let it = iter.next(); !it.done; it = iter.next()) {
                        const [slot, { nodes: slotNodesToInsert, insertPos }] = it.value;
                        Element._$spliceSlotNodes(slot, insertPos, 0, slotNodesToInsert);
                    }
                }
            },
        };
    }
    static insertChildSingleOperation(parent, newChild, oriPosIndex, replace) {
        var _a, _b, _c, _d, _e;
        /* istanbul ignore if  */
        if (newChild && parent.ownerShadowRoot !== newChild.ownerShadowRoot) {
            throw new Error('Cannot move the node from one shadow tree to another shadow tree.');
        }
        let posIndex = oriPosIndex;
        const relChild = posIndex >= 0 ? parent.childNodes[posIndex] : undefined;
        let removal;
        if (replace) {
            if (!relChild) {
                removal = false;
            }
            else if (newChild === relChild) {
                removal = false;
            }
            else {
                removal = true;
            }
        }
        else {
            removal = false;
        }
        if (!removal && !newChild)
            return;
        // change the parent of newChild
        let oldParent;
        if (newChild) {
            oldParent = newChild.parentNode;
            if (oldParent) {
                const childNodes = oldParent.childNodes;
                const oldPosIndex = newChild.parentIndex;
                childNodes.splice(oldPosIndex, 1);
                for (let i = oldPosIndex; i < childNodes.length; i += 1) {
                    childNodes[i].parentIndex = i;
                }
                newChild.parentIndex = -1;
                if (oldParent === parent && oldPosIndex < posIndex)
                    posIndex -= 1;
                const containingSlotUpdater = Element._$updateSubtreeSlotNodes(parent, [newChild], null, Element._$getParentHostShadowRoot(oldParent), posIndex);
                containingSlotUpdater === null || containingSlotUpdater === void 0 ? void 0 : containingSlotUpdater.removeSlotNodes();
                if (BM.DOMLIKE || (BM.DYNAMIC && parent.getBackendMode() === 3 /* BackendMode.Domlike */)) {
                    // removal of in-tree elements are not needed for DOM backend
                    // do nothing
                }
                else if (BM.SHADOW || (BM.DYNAMIC && parent.getBackendMode() === 1 /* BackendMode.Shadow */)) {
                    if (ENV.DEV)
                        performanceMeasureStart('backend.removeChild');
                    (_a = oldParent._$backendElement) === null || _a === void 0 ? void 0 : _a.removeChild(newChild._$backendElement, oldPosIndex);
                    if (ENV.DEV)
                        performanceMeasureEnd();
                }
                else {
                    Element.insertChildComposed(oldParent, null, newChild, true, oldPosIndex);
                }
                containingSlotUpdater === null || containingSlotUpdater === void 0 ? void 0 : containingSlotUpdater.updateContainingSlot();
            }
            newChild.parentNode = parent;
            if (oldParent !== parent) {
                if (oldParent)
                    (_b = oldParent._$mutationObserverTarget) === null || _b === void 0 ? void 0 : _b.detachChild(newChild);
                (_c = parent._$mutationObserverTarget) === null || _c === void 0 ? void 0 : _c.attachChild(newChild);
            }
        }
        else {
            oldParent = null;
        }
        // update containingSlot
        const parentComponentShadowRoot = Element._$getParentHostShadowRoot(parent);
        const newChildContainingSlotUpdater = newChild
            ? Element._$updateSubtreeSlotNodes(parent, [newChild], parentComponentShadowRoot, null, posIndex)
            : null;
        const relChildContainingSlotUpdater = relChild && removal
            ? Element._$updateSubtreeSlotNodes(parent, [relChild], null, parentComponentShadowRoot, posIndex)
            : null;
        newChildContainingSlotUpdater === null || newChildContainingSlotUpdater === void 0 ? void 0 : newChildContainingSlotUpdater.updateContainingSlot();
        relChildContainingSlotUpdater === null || relChildContainingSlotUpdater === void 0 ? void 0 : relChildContainingSlotUpdater.removeSlotNodes();
        // spread in composed tree
        if (BM.SHADOW || (BM.DYNAMIC && parent.getBackendMode() === 1 /* BackendMode.Shadow */)) {
            if (parent._$backendElement) {
                if (removal) {
                    if (newChild) {
                        if (ENV.DEV)
                            performanceMeasureStart('backend.replaceChild');
                        parent._$backendElement.replaceChild(newChild._$backendElement, relChild._$backendElement, posIndex);
                        if (ENV.DEV)
                            performanceMeasureEnd();
                    }
                    else {
                        if (ENV.DEV)
                            performanceMeasureStart('backend.removeChild');
                        parent._$backendElement.removeChild(relChild._$backendElement, posIndex);
                        if (ENV.DEV)
                            performanceMeasureEnd();
                    }
                }
                else {
                    // relChild could equal to newChild and have been removed above
                    const before = parent.childNodes[posIndex];
                    if (before) {
                        if (ENV.DEV)
                            performanceMeasureStart('backend.insertBefore');
                        parent._$backendElement.insertBefore(newChild._$backendElement, before._$backendElement, posIndex);
                        if (ENV.DEV)
                            performanceMeasureEnd();
                    }
                    else {
                        if (ENV.DEV)
                            performanceMeasureStart('backend.appendChild');
                        parent._$backendElement.appendChild(newChild._$backendElement);
                        if (ENV.DEV)
                            performanceMeasureEnd();
                    }
                }
            }
        }
        else {
            Element.insertChildComposed(parent, newChild, relChild, removal, posIndex);
        }
        newChildContainingSlotUpdater === null || newChildContainingSlotUpdater === void 0 ? void 0 : newChildContainingSlotUpdater.insertSlotNodes();
        relChildContainingSlotUpdater === null || relChildContainingSlotUpdater === void 0 ? void 0 : relChildContainingSlotUpdater.updateContainingSlot();
        // remove parent of relChild if needed
        if (removal && relChild) {
            if (isElement(relChild)) {
                (_d = parent._$mutationObserverTarget) === null || _d === void 0 ? void 0 : _d.detachChild(relChild);
            }
            relChild.parentNode = null;
            relChild.parentIndex = -1;
        }
        // handling child nodes list
        const childNodes = parent.childNodes;
        if (newChild) {
            if (posIndex < 0) {
                childNodes.push(newChild);
                newChild.parentIndex = childNodes.length - 1;
            }
            else if (removal) {
                childNodes[posIndex] = newChild;
                newChild.parentIndex = posIndex;
            }
            else {
                childNodes.splice(posIndex, 0, newChild);
                for (let i = posIndex; i < childNodes.length; i += 1) {
                    childNodes[i].parentIndex = i;
                }
            }
        }
        else if (removal) {
            childNodes.splice(posIndex, 1);
            for (let i = posIndex; i < childNodes.length; i += 1) {
                childNodes[i].parentIndex = i;
            }
        }
        // update subtree slots
        const newChildSubtreeSlotStart = newChild ? newChild._$subtreeSlotStart : null;
        const newChildSubtreeSlotEnd = newChild ? newChild._$subtreeSlotEnd : null;
        const relChildSubtreeSlotStart = relChild ? relChild._$subtreeSlotStart : null;
        const relChildSubtreeSlotEnd = relChild ? relChild._$subtreeSlotEnd : null;
        if (newChild) {
            if (oldParent) {
                Element._$updateSubtreeSlotsRemoval(oldParent, newChildSubtreeSlotStart, newChildSubtreeSlotEnd, true);
            }
            if (removal) {
                Element._$updateSubtreeSlotsReplacement(parent, newChildSubtreeSlotStart, newChildSubtreeSlotEnd, relChildSubtreeSlotStart, relChildSubtreeSlotEnd, posIndex, !!oldParent);
            }
            else {
                Element._$updateSubtreeSlotsInsertion(parent, newChildSubtreeSlotStart, newChildSubtreeSlotEnd, posIndex, !!oldParent);
            }
        }
        else if (removal && relChild) {
            Element._$updateSubtreeSlotsRemoval(parent, relChildSubtreeSlotStart, relChildSubtreeSlotEnd, false);
        }
        // update id and slot cache if needed
        (_e = parent.ownerShadowRoot) === null || _e === void 0 ? void 0 : _e._$markIdCacheDirty();
        // call life-times
        if (removal) {
            Element.checkAndCallDetached(relChild);
            Element.checkChildObservers(parent, 'remove', relChild);
        }
        if (newChild) {
            if (oldParent === null || oldParent === void 0 ? void 0 : oldParent._$attached) {
                if (parent._$attached) {
                    Element.checkAndCallMoved(newChild);
                }
                else {
                    Element.checkAndCallDetached(newChild);
                }
            }
            else if (parent._$attached) {
                Element.checkAndCallAttached(newChild);
            }
            if (oldParent === parent) {
                Element.checkChildObservers(parent, 'move', newChild);
            }
            else {
                if (oldParent) {
                    Element.checkChildObservers(oldParent, 'remove', newChild);
                }
                Element.checkChildObservers(parent, 'add', newChild);
            }
        }
    }
    static insertChildBatchRemoval(parent, posIndex, count) {
        var _a, _b;
        const relChild = parent.childNodes[posIndex];
        let parentComponent = parent;
        while (parentComponent === null || parentComponent === void 0 ? void 0 : parentComponent._$inheritSlots)
            parentComponent = parentComponent.parentNode;
        const parentComponentShadowRoot = isComponent(parentComponent) && !parentComponent._$external
            ? parentComponent.shadowRoot
            : null;
        // handling child nodes list
        const childNodes = parent.childNodes;
        const relChildren = childNodes.splice(posIndex, count);
        for (let i = posIndex; i < childNodes.length; i += 1) {
            childNodes[i].parentIndex = i;
        }
        const containingSlotUpdater = Element._$updateSubtreeSlotNodes(parent, relChildren, null, parentComponentShadowRoot, posIndex);
        containingSlotUpdater === null || containingSlotUpdater === void 0 ? void 0 : containingSlotUpdater.removeSlotNodes();
        // spread in composed tree
        if (BM.SHADOW || (BM.DYNAMIC && parent.getBackendMode() === 1 /* BackendMode.Shadow */)) {
            if (parent._$backendElement) {
                if (ENV.DEV)
                    performanceMeasureStart('backend.spliceRemove');
                parent._$backendElement.spliceRemove(relChild._$backendElement, count);
                if (ENV.DEV)
                    performanceMeasureEnd();
            }
        }
        else {
            for (let i = count - 1; i >= 0; i -= 1) {
                Element.insertChildComposed(parent, null, relChildren[i], true, i);
            }
        }
        // remove parent
        for (let i = 0; i < count; i += 1) {
            const relChild = relChildren[i];
            relChild.parentNode = null;
            relChild.parentIndex = -1;
            if (isElement(relChild)) {
                (_a = parent._$mutationObserverTarget) === null || _a === void 0 ? void 0 : _a.detachChild(relChild);
            }
        }
        containingSlotUpdater === null || containingSlotUpdater === void 0 ? void 0 : containingSlotUpdater.updateContainingSlot();
        // update subtree slot
        let subtreeSlotStart = null;
        let subtreeSlotEnd = null;
        for (let i = 0; i < count; i += 1) {
            const relChild = relChildren[i];
            if (!subtreeSlotStart)
                subtreeSlotStart = relChild._$subtreeSlotStart;
            if (relChild._$subtreeSlotEnd)
                subtreeSlotEnd = relChild._$subtreeSlotEnd;
        }
        Element._$updateSubtreeSlotsRemoval(parent, subtreeSlotStart, subtreeSlotEnd, false);
        // update id and slot cache if needed
        (_b = parent.ownerShadowRoot) === null || _b === void 0 ? void 0 : _b._$markIdCacheDirty();
        // call life-times
        for (let i = 0; i < count; i += 1) {
            const relChild = relChildren[i];
            Element.checkAndCallDetached(relChild);
            Element.checkChildObservers(parent, 'remove', relChild);
        }
    }
    static insertChildBatchInsertion(parent, newChildList, posIndex) {
        var _a, _b;
        const relChild = posIndex >= 0 ? parent.childNodes[posIndex] : undefined;
        const actualPosIndex = posIndex >= 0 ? posIndex : parent.childNodes.length;
        // update containingSlot
        let parentComponent = parent;
        while (parentComponent === null || parentComponent === void 0 ? void 0 : parentComponent._$inheritSlots)
            parentComponent = parentComponent.parentNode;
        const parentComponentShadowRoot = isComponent(parentComponent) && !parentComponent._$external
            ? parentComponent.shadowRoot
            : null;
        // change the parent of newChild
        let frag;
        if ((BM.SHADOW || (BM.DYNAMIC && parent.getBackendMode() === 1 /* BackendMode.Shadow */)) &&
            newChildList.length >= 5) {
            const backendContext = parent.getBackendContext();
            frag = backendContext.createFragment();
        }
        else {
            frag = null;
        }
        for (let i = 0; i < newChildList.length; i += 1) {
            const newChild = newChildList[i];
            /* istanbul ignore if  */
            if (parent.ownerShadowRoot !== newChild.ownerShadowRoot) {
                throw new Error('Cannot move the node from one shadow tree to another shadow tree.');
            }
            const oldParent = newChild.parentNode;
            /* istanbul ignore if  */
            if (oldParent) {
                throw new Error('Cannot batch-insert the node which already has a parent.');
            }
            newChild.parentNode = parent;
            (_a = parent._$mutationObserverTarget) === null || _a === void 0 ? void 0 : _a.attachChild(newChild);
            if ((BM.SHADOW || (BM.DYNAMIC && parent.getBackendMode() === 1 /* BackendMode.Shadow */)) && frag) {
                const be = newChild._$backendElement;
                if (ENV.DEV)
                    performanceMeasureStart('backend.appendChild');
                frag.appendChild(be);
                if (ENV.DEV)
                    performanceMeasureEnd();
            }
        }
        // update containingSlot
        const containingSlotUpdater = Element._$updateSubtreeSlotNodes(parent, newChildList, parentComponentShadowRoot, null, posIndex);
        containingSlotUpdater === null || containingSlotUpdater === void 0 ? void 0 : containingSlotUpdater.updateContainingSlot();
        // spread in composed tree
        if (BM.SHADOW || (BM.DYNAMIC && parent.getBackendMode() === 1 /* BackendMode.Shadow */)) {
            if (parent._$backendElement) {
                if (frag) {
                    if (relChild) {
                        if (ENV.DEV)
                            performanceMeasureStart('backend.spliceBefore');
                        parent._$backendElement.spliceBefore(relChild._$backendElement, 0, frag);
                        if (ENV.DEV)
                            performanceMeasureEnd();
                    }
                    else {
                        if (ENV.DEV)
                            performanceMeasureStart('backend.spliceAppend');
                        parent._$backendElement.spliceAppend(frag);
                        if (ENV.DEV)
                            performanceMeasureEnd();
                    }
                    frag.release();
                }
                else {
                    if (relChild) {
                        for (let i = 0; i < newChildList.length; i += 1) {
                            const newChild = newChildList[i];
                            if (ENV.DEV)
                                performanceMeasureStart('backend.insertBefore');
                            parent._$backendElement.insertBefore(newChild._$backendElement, relChild._$backendElement);
                            if (ENV.DEV)
                                performanceMeasureEnd();
                        }
                    }
                    else {
                        for (let i = 0; i < newChildList.length; i += 1) {
                            const newChild = newChildList[i];
                            if (ENV.DEV)
                                performanceMeasureStart('backend.appendChild');
                            parent._$backendElement.appendChild(newChild._$backendElement);
                            if (ENV.DEV)
                                performanceMeasureEnd();
                        }
                    }
                }
            }
        }
        else {
            const pos = posIndex >= 0 ? posIndex : parent.childNodes.length;
            for (let i = 0; i < newChildList.length; i += 1) {
                const newChild = newChildList[i];
                Element.insertChildComposed(parent, newChild, relChild, false, pos);
            }
        }
        containingSlotUpdater === null || containingSlotUpdater === void 0 ? void 0 : containingSlotUpdater.insertSlotNodes();
        // handling child nodes list
        const childNodes = parent.childNodes;
        if (relChild) {
            childNodes.splice(posIndex, 0, ...newChildList);
            for (let i = posIndex; i < childNodes.length; i += 1) {
                childNodes[i].parentIndex = i;
            }
        }
        else {
            childNodes.push(...newChildList);
            for (let i = childNodes.length - newChildList.length; i < childNodes.length; i += 1) {
                childNodes[i].parentIndex = i;
            }
        }
        // update subtree slot
        let subtreeSlotStart = null;
        let subtreeSlotEnd = null;
        for (let i = 0; i < newChildList.length; i += 1) {
            const newChild = newChildList[i];
            const newChildSubtreeSlotStart = newChild._$subtreeSlotStart;
            const newChildSubtreeSlotEnd = newChild._$subtreeSlotEnd;
            if (newChildSubtreeSlotStart) {
                if (subtreeSlotEnd) {
                    newChildSubtreeSlotStart.prev = subtreeSlotEnd;
                    subtreeSlotEnd.next = newChildSubtreeSlotStart;
                    subtreeSlotEnd = newChildSubtreeSlotEnd;
                }
                else {
                    subtreeSlotStart = newChildSubtreeSlotStart;
                    subtreeSlotEnd = newChildSubtreeSlotEnd;
                }
            }
        }
        Element._$updateSubtreeSlotsInsertion(parent, subtreeSlotStart, subtreeSlotEnd, actualPosIndex + newChildList.length - 1, false);
        // update id and slot cache if needed
        (_b = parent.ownerShadowRoot) === null || _b === void 0 ? void 0 : _b._$markIdCacheDirty();
        // call life-times
        for (let i = 0; i < newChildList.length; i += 1) {
            const newChild = newChildList[i];
            if (parent._$attached) {
                Element.checkAndCallAttached(newChild);
            }
            Element.checkChildObservers(parent, 'add', newChild);
        }
    }
    static insertChildPlaceholderReplace(parent, posIndex, replacer) {
        var _a, _b, _c, _d, _e, _f;
        /* istanbul ignore if  */
        if (replacer && parent.ownerShadowRoot !== replacer.ownerShadowRoot) {
            throw new Error('Cannot move the node from one shadow tree to another shadow tree.');
        }
        /* istanbul ignore if  */
        if (replacer.parentNode) {
            throw new Error('Cannot replace with the node which already has a parent.');
        }
        const placeholder = parent.childNodes[posIndex];
        /* istanbul ignore if  */
        if (!isElement(placeholder)) {
            throw new Error('Cannot replace on text nodes.');
        }
        /* istanbul ignore if  */
        if (placeholder._$slotName !== null || replacer._$slotName !== null) {
            throw new Error('Cannot replace on slot nodes.');
        }
        if (placeholder === replacer)
            return;
        // change the parent of replacer's children
        let frag = null;
        const replacedChildren = placeholder.childNodes;
        if (BM.SHADOW || (BM.DYNAMIC && parent.getBackendMode() === 1 /* BackendMode.Shadow */)) {
            if (replacedChildren.length > 0) {
                const be = placeholder._$backendElement;
                if (be) {
                    if (ENV.DEV)
                        performanceMeasureStart('backend.spliceRemove');
                    be.spliceRemove(replacedChildren[0]._$backendElement, replacedChildren.length);
                    if (ENV.DEV)
                        performanceMeasureEnd();
                }
            }
        }
        for (let i = 0; i < replacedChildren.length; i += 1) {
            const child = replacedChildren[i];
            if (BM.DOMLIKE || (BM.DYNAMIC && parent.getBackendMode() === 3 /* BackendMode.Domlike */)) {
                // removal of in-tree elements are not needed for DOM backend
                // do nothing
            }
            else if (BM.SHADOW || (BM.DYNAMIC && parent.getBackendMode() === 1 /* BackendMode.Shadow */)) {
                if (!frag) {
                    const backendContext = parent.getBackendContext();
                    if (ENV.DEV)
                        performanceMeasureStart('backend.createFragment');
                    frag = backendContext.createFragment();
                    if (ENV.DEV)
                        performanceMeasureEnd();
                }
                const be = child._$backendElement;
                if (ENV.DEV)
                    performanceMeasureStart('backend.appendChild');
                frag.appendChild(be);
                if (ENV.DEV)
                    performanceMeasureEnd();
            }
            else {
                Element.insertChildComposed(placeholder, null, child, true, i);
            }
            child.parentNode = replacer;
            (_a = placeholder._$mutationObserverTarget) === null || _a === void 0 ? void 0 : _a.detachChild(child);
            (_b = parent._$mutationObserverTarget) === null || _b === void 0 ? void 0 : _b.attachChild(child);
        }
        // handling child nodes list for placeholder
        placeholder.childNodes = [];
        // change the parent
        placeholder.parentNode = null;
        placeholder.parentIndex = -1;
        replacer.parentNode = parent;
        // update containingSlot
        const placeholderContainingSlot = placeholder.containingSlot;
        const placeholderSlotIndex = placeholder.slotIndex;
        if (placeholderContainingSlot !== undefined) {
            Element._$updateContainingSlot(replacer, placeholderContainingSlot);
            if (placeholderContainingSlot) {
                Element._$spliceSlotNodes(placeholderContainingSlot, placeholderSlotIndex, 1, undefined);
            }
        }
        const containingSlotUpdater = replacedChildren.length
            ? Element._$updateSubtreeSlotNodes(replacer, replacedChildren, Element._$getParentHostShadowRoot(replacer), Element._$getParentHostShadowRoot(placeholder), 0)
            : null;
        containingSlotUpdater === null || containingSlotUpdater === void 0 ? void 0 : containingSlotUpdater.updateContainingSlot();
        containingSlotUpdater === null || containingSlotUpdater === void 0 ? void 0 : containingSlotUpdater.removeSlotNodes();
        // handling child nodes list for parent
        parent.childNodes[posIndex] = replacer;
        replacer.parentIndex = posIndex;
        // spread in composed tree
        if (BM.SHADOW || (BM.DYNAMIC && parent.getBackendMode() === 1 /* BackendMode.Shadow */)) {
            if (parent._$backendElement) {
                if (ENV.DEV)
                    performanceMeasureStart('backend.replaceChild');
                parent._$backendElement.replaceChild(replacer._$backendElement, placeholder._$backendElement, posIndex);
                if (ENV.DEV)
                    performanceMeasureEnd();
                if (frag) {
                    if (ENV.DEV)
                        performanceMeasureStart('backend.spliceAppend');
                    (_c = replacer._$backendElement) === null || _c === void 0 ? void 0 : _c.spliceAppend(frag);
                    if (ENV.DEV)
                        performanceMeasureEnd();
                }
            }
            if (frag) {
                if (ENV.DEV)
                    performanceMeasureStart('backend.release');
                frag.release();
                if (ENV.DEV)
                    performanceMeasureEnd();
            }
        }
        else {
            Element.insertChildComposed(parent, replacer, placeholder, true, posIndex);
            for (let i = 0; i < replacedChildren.length; i += 1) {
                const child = replacedChildren[i];
                Element.insertChildComposed(replacer, child, undefined, false, i);
            }
        }
        containingSlotUpdater === null || containingSlotUpdater === void 0 ? void 0 : containingSlotUpdater.insertSlotNodes();
        if (placeholderContainingSlot !== undefined) {
            Element._$updateContainingSlot(placeholder, undefined);
            if (placeholderContainingSlot) {
                Element._$spliceSlotNodes(placeholderContainingSlot, placeholderSlotIndex, 0, [replacer]);
            }
        }
        // update subtree slots
        // (assume that placeholder and replacer will never be slot node)
        replacer._$subtreeSlotStart = placeholder._$subtreeSlotStart;
        replacer._$subtreeSlotEnd = placeholder._$subtreeSlotEnd;
        (_d = parent._$mutationObserverTarget) === null || _d === void 0 ? void 0 : _d.detachChild(placeholder);
        (_e = parent._$mutationObserverTarget) === null || _e === void 0 ? void 0 : _e.attachChild(replacer);
        // handling child nodes list for replacer
        replacer.childNodes.push(...replacedChildren);
        for (let i = replacer.childNodes.length - replacedChildren.length; i < replacer.childNodes.length; i += 1) {
            replacer.childNodes[i].parentIndex = i;
        }
        // update id and slot cache if needed
        (_f = parent.ownerShadowRoot) === null || _f === void 0 ? void 0 : _f._$markIdCacheDirty();
        // call life-times
        if (parent._$attached) {
            Element.checkAndCallDetached(placeholder);
            Element.checkChildObservers(parent, 'remove', placeholder);
            Element.checkAndCallAttached(replacer);
            Element.checkChildObservers(parent, 'add', replacer);
            for (let i = 0; i < replacedChildren.length; i += 1) {
                const child = replacedChildren[i];
                Element.checkAndCallMoved(child);
                Element.checkChildObservers(parent, 'move', child);
            }
        }
    }
    appendChild(child) {
        Element.insertChildSingleOperation(this, child, this.childNodes.length, false);
    }
    insertChildAt(child, index) {
        Element.insertChildSingleOperation(this, child, index, false);
    }
    insertBefore(child, before) {
        const index = before ? before.parentIndex : -1;
        Element.insertChildSingleOperation(this, child, index, false);
    }
    removeChildAt(index) {
        Element.insertChildSingleOperation(this, null, index, true);
    }
    removeChild(child) {
        const index = child.parentIndex;
        Element.insertChildSingleOperation(this, null, index, true);
    }
    replaceChildAt(child, index) {
        Element.insertChildSingleOperation(this, child, index, true);
    }
    replaceChild(child, relChild) {
        const index = relChild.parentIndex;
        Element.insertChildSingleOperation(this, child, index, true);
    }
    insertChildren(children, index) {
        Element.insertChildBatchInsertion(this, children, index);
    }
    removeChildren(index, count) {
        Element.insertChildBatchRemoval(this, index, count);
    }
    selfReplaceWith(replaceWith) {
        const parent = this.parentNode;
        if (parent) {
            Element.insertChildPlaceholderReplace(parent, this.parentIndex, replaceWith);
        }
    }
    /** @internal */
    static _$generateIdMap(node) {
        const idMap = Object.create(null);
        const dfs = function dfs(node) {
            if (isElement(node)) {
                const nodeId = node._$nodeId;
                if (nodeId) {
                    if (!idMap[nodeId])
                        idMap[nodeId] = node;
                }
                node.childNodes.forEach(dfs);
            }
        };
        dfs(node);
        return idMap;
    }
    /** Trigger an event on the element */
    triggerEvent(name, detail, options) {
        Event.triggerEvent(this, name, detail, options);
    }
    /** Trigger an event with specified event object on the element */
    dispatchEvent(ev) {
        Event.dispatchEvent(this, ev);
    }
    /* @internal */
    _$setListenerStats(name, finalChanged, options = {}) {
        if (!this._$backendElement)
            return;
        const capture = !!options.capture || !!options.useCapture;
        let mutLevel;
        switch (finalChanged) {
            case 2 /* FinalChanged.None */:
                mutLevel = 0 /* MutLevel.None */;
                break;
            case 4 /* FinalChanged.Mut */:
                mutLevel = 1 /* MutLevel.Mut */;
                break;
            case 3 /* FinalChanged.Final */:
                mutLevel = 2 /* MutLevel.Final */;
                break;
            default:
                return;
        }
        if (this._$nodeTreeContext) {
            if (ENV.DEV)
                performanceMeasureStart('backend.setListenerStats');
            if (BM.DOMLIKE || (BM.DYNAMIC && this.getBackendMode() === 3 /* BackendMode.Domlike */)) {
                ;
                this._$nodeTreeContext.setListenerStats(this._$backendElement, name, capture, mutLevel);
            }
            else {
                ;
                this._$backendElement.setListenerStats(name, capture, mutLevel);
            }
            if (ENV.DEV)
                performanceMeasureEnd();
        }
    }
    /** Add an event listener on the element */
    addListener(name, func, options) {
        const finalChanged = this._$eventTarget.addListener(name, func, options);
        this._$setListenerStats(name, finalChanged, options);
        if (isComponent(this) && this._$definition._$options.listenerChangeLifetimes) {
            this.triggerLifetime('listenerChange', [true, name, func, options]);
        }
    }
    /** Remove an event listener on the element */
    removeListener(name, func, options) {
        const finalChanged = this._$eventTarget.removeListener(name, func, options);
        if (finalChanged === 1 /* FinalChanged.Failed */)
            return;
        this._$setListenerStats(name, finalChanged, options);
        if (isComponent(this) && this._$definition._$options.listenerChangeLifetimes) {
            this.triggerLifetime('listenerChange', [false, name, func, options]);
        }
    }
    getListeners() {
        return this._$eventTarget.getListeners();
    }
    /** Get an attribute value ( `null` if not set or removed) */
    getAttribute(name) {
        if (!this._$nodeAttributes)
            return null;
        if (!Object.prototype.hasOwnProperty.call(this._$nodeAttributes, name))
            return null;
        return this._$nodeAttributes[name];
    }
    /** Update an attribute value */
    updateAttribute(name, value) {
        this.setAttribute(name, value);
    }
    /** Set an attribute value */
    setAttribute(name, value) {
        let attrs;
        if (this._$nodeAttributes) {
            attrs = this._$nodeAttributes;
        }
        else {
            attrs = Object.create(null);
            this._$nodeAttributes = attrs;
        }
        attrs[name] = value;
        const be = this._$backendElement;
        if (be) {
            if (ENV.DEV)
                performanceMeasureStart('backend.setAttribute');
            if (BM.DOMLIKE || (BM.DYNAMIC && this.getBackendMode() === 3 /* BackendMode.Domlike */)) {
                if (value === false) {
                    be.removeAttribute(name);
                }
                else {
                    be.setAttribute(name, value === true || value === undefined || value === null ? '' : String(value));
                }
            }
            else {
                be.setAttribute(name, value);
            }
            if (ENV.DEV)
                performanceMeasureEnd();
        }
        if (this._$mutationObserverTarget) {
            MutationObserverTarget.callAttrObservers(this, {
                type: 'properties',
                target: this,
                nameType: 'attribute',
                attributeName: name,
            });
        }
    }
    /** Remove an attribute */
    removeAttribute(name) {
        if (this._$nodeAttributes) {
            delete this._$nodeAttributes[name];
        }
        const be = this._$backendElement;
        if (be) {
            if (ENV.DEV)
                performanceMeasureStart('backend.removeAttribute');
            be.removeAttribute(name);
            if (ENV.DEV)
                performanceMeasureEnd();
        }
        if (this._$mutationObserverTarget) {
            MutationObserverTarget.callAttrObservers(this, {
                type: 'properties',
                target: this,
                nameType: 'attribute',
                attributeName: name,
            });
        }
    }
    /** Set a dataset on the element */
    setDataset(name, value) {
        this.dataset[name] = value;
        if (this._$backendElement) {
            if (BM.SHADOW || (BM.DYNAMIC && this.getBackendMode() === 1 /* BackendMode.Shadow */)) {
                if (ENV.DEV)
                    performanceMeasureStart('backend.setDataset');
                this._$backendElement.setDataset(name, value);
                if (ENV.DEV)
                    performanceMeasureEnd();
            }
        }
        if (this._$mutationObserverTarget) {
            MutationObserverTarget.callAttrObservers(this, {
                type: 'properties',
                target: this,
                nameType: 'dataset',
                attributeName: `data:${name}`,
            });
        }
    }
    /** Set a mark on the element */
    setMark(name, value) {
        if (!this._$marks) {
            const marks = {};
            marks[name] = value;
            this._$marks = marks;
        }
        else {
            this._$marks[name] = value;
        }
        if (this._$mutationObserverTarget) {
            MutationObserverTarget.callAttrObservers(this, {
                type: 'properties',
                target: this,
                nameType: 'mark',
                attributeName: `mark:${name}`,
            });
        }
    }
    /**
     * Collect the marks on the element
     *
     * The marks includes the marks on ancestors (in shadow tree) of the element.
     * If multiple marks on different elements shares the same name,
     * the mark value on the child-most element is accepted.
     */
    collectMarks() {
        const ret = {};
        let cur;
        for (cur = this; cur; cur = cur.parentNode) {
            const marks = cur._$marks;
            if (marks) {
                Object.keys(marks).forEach((name) => {
                    if (!Object.prototype.hasOwnProperty.call(ret, name)) {
                        ret[name] = marks[name];
                    }
                });
            }
        }
        return ret;
    }
    /**
     * Attach the element into the backend, swapping out a placeholder element in the backend.
     *
     * The `element` must not be a child node of another element,
     * must not be attached before,
     * and must not have a `ownerShadowRoot` .
     * The `element` `targetParent` and `targetNode` must be in the same backend context.
     * The `element` replaces the `targetNode` in the `targetParent` .
     */
    static replaceDocumentElement(element, targetParent, targetNode) {
        /* istanbul ignore if  */
        if (element._$attached) {
            throw new Error('An attached element cannot be attached again');
        }
        if (element._$backendElement) {
            if (ENV.DEV)
                performanceMeasureStart('backend.replaceChild');
            targetParent.replaceChild(element._$backendElement, targetNode);
            if (ENV.DEV)
                performanceMeasureEnd();
        }
        Element.checkAndCallAttached(element);
        if (ENV.DEV)
            attachInspector(element);
    }
    /**
     * Make the element looks like attached.
     *
     * If the element will never be attached to backend or it has no backend element at all,
     * this can be used to trigger `attached` life-time.
     */
    static pretendAttached(element) {
        if (!element._$attached) {
            Element.checkAndCallAttached(element);
        }
        if (ENV.DEV)
            attachInspector(element);
    }
    /**
     * Make the element looks like detached.
     *
     * This can be used to trigger `detached` life-time without remove the element in the backend.
     */
    static pretendDetached(element) {
        if (element._$attached) {
            Element.checkAndCallDetached(element);
        }
        if (ENV.DEV)
            detachInspector(element);
    }
    /** Check the element is attached or not */
    static isAttached(element) {
        return element._$attached;
    }
    /**
     * Set the slot name of the element
     *
     * Once this method is called for an `element` ,
     * it will be treated as a slot which can contain child nodes in composed tree.
     * This method should not be used in components,
     * otherwise the slot content will always be dangled.
     */
    static setSlotName(element, name) {
        var _a;
        /* istanbul ignore if  */
        if (element._$inheritSlots) {
            throw new Error('Slot-inherit mode is not usable in slot element');
        }
        const slotName = name ? String(name) : '';
        const oldSlotName = element._$slotName;
        if (oldSlotName === slotName)
            return;
        const needInsertSlot = oldSlotName === null;
        element._$slotName = slotName;
        if (needInsertSlot) {
            element._$subtreeSlotStart = element._$subtreeSlotEnd = {
                value: element,
                prev: null,
                next: null,
            };
        }
        if (BM.SHADOW || (BM.DYNAMIC && element.getBackendMode() === 1 /* BackendMode.Shadow */)) {
            if (ENV.DEV)
                performanceMeasureStart('backend.setSlotName');
            (_a = element._$backendElement) === null || _a === void 0 ? void 0 : _a.setSlotName(slotName);
            if (ENV.DEV)
                performanceMeasureEnd();
        }
        const owner = element.ownerShadowRoot;
        if (owner) {
            if (needInsertSlot) {
                if (owner.getSlotMode() === 3 /* SlotMode.Dynamic */)
                    element._$slotValues = Object.create(null);
                element.slotNodes = [];
                const parent = element.parentNode;
                if (parent) {
                    Element._$updateSubtreeSlotsInsertion(parent, element._$subtreeSlotStart, element._$subtreeSlotEnd, element.parentIndex, false);
                }
            }
            else {
                if (owner.isConnected(element))
                    owner._$applySlotRename(element, slotName, oldSlotName);
            }
        }
        if (element._$mutationObserverTarget) {
            MutationObserverTarget.callAttrObservers(element, {
                type: 'properties',
                target: element,
                nameType: 'basic',
                attributeName: 'name',
            });
        }
    }
    /**
     * Get the slot name of the element
     */
    static getSlotName(element) {
        const sn = element._$slotName;
        return sn === null ? undefined : sn;
    }
    /**
     * Set the virtual node to slot-inherit mode
     *
     * In slot-inherit mode of an element,
     * the child nodes of the element will be treated as siblings and can have different target slot.
     */
    static setInheritSlots(element) {
        /* istanbul ignore if  */
        if (!element._$virtual) {
            throw new Error('Cannot set slot-inherit on non-virtual node');
        }
        /* istanbul ignore if  */
        if (element._$slotName !== null || element.childNodes.length !== 0) {
            throw new Error('Slot-inherit mode cannot be set when the element has any child node');
        }
        if (BM.SHADOW || (BM.DYNAMIC && element.getBackendMode() === 1 /* BackendMode.Shadow */)) {
            const be = element._$backendElement;
            if (be) {
                if (ENV.DEV)
                    performanceMeasureStart('backend.setInheritSlots');
                be.setInheritSlots();
                if (ENV.DEV)
                    performanceMeasureEnd();
            }
        }
        element._$inheritSlots = true;
    }
    /** Get whether the slot-inherit mode is set or not */
    static getInheritSlots(element) {
        return element._$inheritSlots;
    }
    /** Get whether the slot-inherit mode is set or not */
    isInheritSlots() {
        return this._$inheritSlots;
    }
    /**
     * Set the binding slot of specific node
     *
     * Necessary if node belongs to a dynamic slot, which cannot be identified by slot name.
     */
    static setSlotElement(node, slot) {
        var _a;
        const oldSlotElement = node._$slotElement;
        if (oldSlotElement === slot)
            return;
        node._$slotElement = slot;
        if (BM.SHADOW ||
            (BM.DYNAMIC && node.ownerShadowRoot.getBackendMode() === 1 /* BackendMode.Shadow */)) {
            if (ENV.DEV)
                performanceMeasureStart('backend.setSlotName');
            (_a = node._$backendElement) === null || _a === void 0 ? void 0 : _a.setSlotElement(slot ? slot.getBackendElement() : null);
            if (ENV.DEV)
                performanceMeasureEnd();
        }
        const slotParentShadowRoot = Element._$getParentHostShadowRoot(node.parentNode);
        if (slotParentShadowRoot) {
            const containingSlotUpdater = Element._$updateSubtreeSlotNodes(node.parentNode, [node], slotParentShadowRoot, slotParentShadowRoot, node.parentIndex);
            const oldSlot = node.containingSlot;
            containingSlotUpdater === null || containingSlotUpdater === void 0 ? void 0 : containingSlotUpdater.updateContainingSlot();
            containingSlotUpdater === null || containingSlotUpdater === void 0 ? void 0 : containingSlotUpdater.removeSlotNodes();
            const newSlot = node.containingSlot;
            const recv = (node) => {
                if (isElement(node) && node._$inheritSlots) {
                    for (let i = 0; i < node.childNodes.length; i += 1) {
                        recv(node.childNodes[i]);
                    }
                }
                else {
                    Element.insertChildReassign(node.parentNode, node, oldSlot, newSlot, node.parentIndex);
                }
            };
            recv(node);
            containingSlotUpdater === null || containingSlotUpdater === void 0 ? void 0 : containingSlotUpdater.insertSlotNodes();
        }
    }
    /**
     * Get the binding slot of specific node
     */
    static getSlotElement(node) {
        return node._$slotElement;
    }
    static _$updateContainingSlot(node, containingSlot) {
        node.containingSlot = containingSlot;
    }
    static _$spliceSlotNodes(slot, before, deleteCount, insertion) {
        const slotNodes = (slot.slotNodes = slot.slotNodes || []);
        const spliceBefore = before >= 0 && before < slotNodes.length;
        if (insertion === null || insertion === void 0 ? void 0 : insertion.length) {
            if (spliceBefore) {
                for (let i = before; i < before + deleteCount; i += 1) {
                    slotNodes[i].slotIndex = undefined;
                }
                slotNodes.splice(before, deleteCount, ...insertion);
                for (let i = before; i < slotNodes.length; i += 1) {
                    slotNodes[i].slotIndex = i;
                }
            }
            else {
                const start = slotNodes.length;
                slotNodes.push(...insertion);
                for (let i = start; i < slotNodes.length; i += 1) {
                    slotNodes[i].slotIndex = i;
                }
            }
        }
        else if (deleteCount) {
            for (let i = before; i < before + deleteCount; i += 1) {
                slotNodes[i].slotIndex = undefined;
            }
            slotNodes.splice(before, deleteCount);
            for (let i = before; i < slotNodes.length; i += 1) {
                slotNodes[i].slotIndex = i;
            }
        }
    }
    /** @internal */
    static _$findSlotNodeInsertPosition(slot, target, newPosIndex) {
        const host = slot.ownerShadowRoot.getHostNode();
        const getNodeDepth = (node) => {
            let depth = 0;
            let cur = node;
            while (cur && cur !== host) {
                depth += 1;
                cur = cur.parentNode;
            }
            return depth;
        };
        const targetDepth = getNodeDepth(target);
        const comparePos = (slotNode1, parentIndex1, slotDepth1, slotNode2, parentIndex2, slotDepth2, posIndex) => {
            if (slotNode1 === slotNode2)
                return 0;
            if (slotNode1.parentNode === slotNode2.parentNode) {
                return parentIndex1 - (parentIndex2 === -1 ? posIndex : parentIndex2);
            }
            if (slotDepth2 < slotDepth1) {
                const nextSlotNode1 = slotNode1.parentNode;
                return comparePos(nextSlotNode1, nextSlotNode1.parentIndex, slotDepth1 - 1, slotNode2, parentIndex2, slotDepth2, posIndex);
            }
            const left = slotDepth1 < slotDepth2 ? slotNode1 : slotNode1.parentNode;
            const leftDepth = slotDepth1 < slotDepth2 ? slotDepth1 : slotDepth1 - 1;
            const right = slotNode2.parentNode;
            const rightDepth = slotDepth2 - 1;
            const rst = comparePos(left, left.parentIndex, leftDepth, right, right.parentIndex, rightDepth, posIndex);
            if (rst === 0)
                return -1;
            return rst;
        };
        const slotNodes = slot.slotNodes;
        let i = slotNodes.length - 1;
        for (; i >= 0; i -= 1) {
            const slotNode = slotNodes[i];
            if (comparePos(slotNode, slotNode.parentIndex, getNodeDepth(slotNode), target, target.parentIndex, targetDepth, newPosIndex >= 0
                ? newPosIndex - 0.5
                : // negative posIndex (such as -1) means to insert at the tail
                    slotNodes.length) < 0)
                break;
        }
        const insertPos = i + 1;
        return insertPos;
    }
    /** Get composed parent (including virtual nodes) */
    getComposedParent() {
        if (isShadowRoot(this))
            return this.getHostNode();
        if (this.containingSlot !== undefined)
            return this.containingSlot;
        let parent = this.parentNode;
        while (parent === null || parent === void 0 ? void 0 : parent._$inheritSlots) {
            parent = parent.parentNode;
        }
        return parent;
    }
    /**
     * Get the composed children
     *
     * This method always returns a new array.
     * It is convenient but less performant.
     * For better performance, consider using `forEachComposedChild` .
     */
    getComposedChildren() {
        const ret = [];
        this.forEachComposedChild((child) => {
            ret.push(child);
        });
        return ret;
    }
    /**
     * Iterate composed child nodes (including virtual nodes)
     *
     * if `f` returns `false` then the iteration is interrupted.
     * Returns `true` if that happens.
     */
    forEachComposedChild(f) {
        if (this._$inheritSlots)
            return true;
        if (isComponent(this) && !this._$external) {
            return f(this.shadowRoot) !== false;
        }
        if (this._$slotName !== null) {
            const ownerShadowRoot = this.ownerShadowRoot;
            if (!ownerShadowRoot)
                return true;
            return ownerShadowRoot.forEachNodeInSpecifiedSlot(this, f);
        }
        const recInheritSlots = (children) => {
            for (let i = 0; i < children.length; i += 1) {
                const child = children[i];
                if (f(child) === false)
                    return false;
                if (child._$inheritSlots) {
                    if (!recInheritSlots(child.childNodes))
                        return false;
                }
            }
            return true;
        };
        return recInheritSlots(this.childNodes);
    }
    /**
     * Iterate composed child nodes (including virtual nodes)
     *
     * if `f` returns `false` then the iteration is interrupted.
     * Returns `true` if that happens.
     */
    *iterateComposedChild() {
        if (this._$inheritSlots)
            return;
        if (isComponent(this) && !this._$external) {
            yield this.shadowRoot;
            return;
        }
        if (this._$slotName !== null) {
            const ownerShadowRoot = this.ownerShadowRoot;
            if (!ownerShadowRoot)
                return;
            const childNodes = this.slotNodes;
            for (let i = 0; i < childNodes.length; i += 1) {
                yield childNodes[i];
            }
            return;
        }
        const recInheritSlots = function* (children) {
            for (let i = 0; i < children.length; i += 1) {
                const child = children[i];
                yield child;
                if (child._$inheritSlots) {
                    yield* recInheritSlots(child.childNodes);
                }
            }
        };
        yield* recInheritSlots(this.childNodes);
    }
    /**
     * Iterate non-virtual composed child nodes
     *
     * if `f` returns `false` then the iteration is interrupted.
     * Returns `true` if that happens.
     */
    forEachNonVirtualComposedChild(f) {
        if (this._$inheritSlots)
            return true;
        const recNonVirtual = (child) => {
            if (child._$virtual) {
                return child.forEachNonVirtualComposedChild(f);
            }
            return f(child) !== false;
        };
        if (isComponent(this) && !this._$external) {
            return recNonVirtual(this.shadowRoot);
        }
        if (this._$slotName !== null) {
            const ownerShadowRoot = this.ownerShadowRoot;
            if (!ownerShadowRoot)
                return true;
            return ownerShadowRoot.forEachSlotContentInSpecifiedSlot(this, recNonVirtual);
        }
        const recInheritSlots = (children) => {
            for (let i = 0; i < children.length; i += 1) {
                const child = children[i];
                if (!recNonVirtual(child))
                    return false;
                if (child._$inheritSlots) {
                    if (!recInheritSlots(child.childNodes))
                        return false;
                }
            }
            return true;
        };
        return recInheritSlots(this.childNodes);
    }
    /** Parse a selector string so that it can be used multiple queries */
    static parseSelector(str) {
        return new ParsedSelector(str);
    }
    /** Select the first descendant which matches the selector */
    querySelector(selectorStr) {
        const parsedSelector = selectorStr instanceof ParsedSelector ? selectorStr : new ParsedSelector(selectorStr);
        const ret = parsedSelector.query(this, true);
        return ret;
    }
    /** Select all descendants which matches the selector */
    querySelectorAll(selectorStr) {
        const parsedSelector = selectorStr instanceof ParsedSelector ? selectorStr : new ParsedSelector(selectorStr);
        const ret = parsedSelector.query(this, false);
        return ret;
    }
    /** Test whether the target matches the selector */
    static matchSelector(selectorStr, target) {
        const parsedSelector = selectorStr instanceof ParsedSelector ? selectorStr : new ParsedSelector(selectorStr);
        return parsedSelector.testSelector(null, target);
    }
    /** Test whether the target in this subtree matches the selector */
    matchSelector(selectorStr, target) {
        const parsedSelector = selectorStr instanceof ParsedSelector ? selectorStr : new ParsedSelector(selectorStr);
        return parsedSelector.testSelector(this, target);
    }
    /**
     * Get the bounding client rect
     *
     * Return zero values when the backend element is invalid or it does not have layout information.
     */
    getBoundingClientRect(cb) {
        var _a;
        const backendElement = this._$backendElement;
        if (!backendElement) {
            setTimeout(() => {
                cb({
                    left: 0,
                    top: 0,
                    width: 0,
                    height: 0,
                });
            }, 0);
            return;
        }
        if (BM.DOMLIKE || (BM.DYNAMIC && this.getBackendMode() === 3 /* BackendMode.Domlike */)) {
            const e = backendElement;
            const res = ((_a = e.getBoundingClientRect) === null || _a === void 0 ? void 0 : _a.call(e)) || { left: 0, top: 0, width: 0, height: 0 };
            setTimeout(() => {
                cb(res);
            }, 0);
        }
        else {
            const be = backendElement;
            if (be.getBoundingClientRect) {
                be.getBoundingClientRect(cb);
            }
            else {
                setTimeout(() => {
                    cb({
                        left: 0,
                        top: 0,
                        width: 0,
                        height: 0,
                    });
                }, 0);
            }
        }
    }
    /**
     * Get the bounding client rect
     *
     * Return zero values when the backend element is invalid or it does not have layout information.
     */
    getScrollOffset(cb) {
        const backendElement = this._$backendElement;
        if (!backendElement) {
            setTimeout(() => {
                cb({
                    scrollLeft: 0,
                    scrollTop: 0,
                    scrollWidth: 0,
                    scrollHeight: 0,
                });
            }, 0);
            return;
        }
        if (BM.DOMLIKE || (BM.DYNAMIC && this.getBackendMode() === 3 /* BackendMode.Domlike */)) {
            const e = backendElement;
            const res = {
                scrollLeft: e.scrollLeft || 0,
                scrollTop: e.scrollTop || 0,
                scrollWidth: e.scrollWidth || 0,
                scrollHeight: e.scrollHeight || 0,
            };
            setTimeout(() => {
                cb(res);
            }, 0);
        }
        else {
            const be = backendElement;
            if (be.getScrollOffset) {
                be.getScrollOffset(cb);
            }
            else {
                setTimeout(() => {
                    cb({
                        scrollLeft: 0,
                        scrollTop: 0,
                        scrollWidth: 0,
                        scrollHeight: 0,
                    });
                }, 0);
            }
        }
    }
    /**
     * Create an intersection observer
     *
     * The `relativeElement` is the element to calculate intersection with ( `null` for the viewport).
     * The `relativeElementMargin` is the margins of the `relativeElement` .
     * The `thresholds` is a list of intersection ratios to trigger the `listener` .
     * The listener always triggers once immediately after this call.
     */
    createIntersectionObserver(relativeElement, relativeElementMargin, thresholds, listener) {
        const backendElement = this._$backendElement;
        if (backendElement) {
            if (relativeElement && !relativeElement._$backendElement) {
                return null;
            }
            if (BM.DOMLIKE || (BM.DYNAMIC && this.getBackendMode() === 3 /* BackendMode.Domlike */)) {
                const context = this._$nodeTreeContext;
                if (!context || !context.createIntersectionObserver)
                    return null;
                return context.createIntersectionObserver(backendElement, (relativeElement === null || relativeElement === void 0 ? void 0 : relativeElement._$backendElement) || null, relativeElementMargin, thresholds, listener);
            }
            const be = backendElement;
            if (!be.createIntersectionObserver)
                return null;
            return be.createIntersectionObserver((relativeElement === null || relativeElement === void 0 ? void 0 : relativeElement._$backendElement) || null, relativeElementMargin, thresholds, listener);
        }
        return null;
    }
    /**
     * Create a resize observer
     *
     * It is possible to choose to observe either the content box or the border box.
     */
    createResizeObserver(mode, listener) {
        const backendElement = this._$backendElement;
        if (backendElement) {
            if (BM.DOMLIKE || (BM.DYNAMIC && this.getBackendMode() === 3 /* BackendMode.Domlike */)) {
                const context = this._$nodeTreeContext;
                if (!context || !context.createResizeObserver)
                    return null;
                return context.createResizeObserver(backendElement, mode, listener);
            }
            const be = backendElement;
            if (!be.createResizeObserver)
                return null;
            return be.createResizeObserver(mode, listener);
        }
        return null;
    }
    /**
     * Get an interactive context
     */
    getContext(cb) {
        const backendElement = this._$backendElement;
        if (!backendElement)
            return;
        if (BM.DOMLIKE || (BM.DYNAMIC && this.getBackendMode() === 3 /* BackendMode.Domlike */)) {
            const context = this._$nodeTreeContext;
            if (context === null || context === void 0 ? void 0 : context.getContext) {
                context.getContext(backendElement, cb);
            }
            else {
                cb(null);
            }
        }
        else {
            const be = backendElement;
            if (be.getContext) {
                be.getContext(cb);
            }
            else {
                cb(null);
            }
        }
    }
}
Element.isElement = isElement;
/** @internal */
Element._$getParentHostShadowRoot = (parent) => {
    let parentSlotHost = parent;
    while (parentSlotHost === null || parentSlotHost === void 0 ? void 0 : parentSlotHost._$inheritSlots)
        parentSlotHost = parentSlotHost.parentNode;
    return isComponent(parentSlotHost) && !parentSlotHost._$external
        ? parentSlotHost.shadowRoot
        : null;
};
Element.prototype[ELEMENT_SYMBOL] = true;
//# sourceMappingURL=element.js.map